import { preflight, type BlockReason } from '../shared/generationPreflight'
/**
 * VideoGeneratorNode (Canvas V2)
 *
 * Generate video clips from reference images and motion prompts.
 * Features:
 * - Duration slider (fixed 8s Veo 3.1)
 * - Motion prompt dual-mode (read: timestamp pills, camera badges,
 *   dialogue cards; edit: textarea)
 * - Inline video preview with hover play, controls on hover
 * - Toolbar: Generate, Expand, Download, Duplicate, Delete
 * - UnifiedBottomPanel (Config; Variants opens from QuickInfo)
 * - Video lightbox (portal, z-9999)
 * - History with inline players, pending spinners
 * - Model-specific duration snapping
 * - 6 connection handles at specified positions
 */

import { useCanvasReferencePicker } from '../../CanvasReferencePicking'
import { memo, useState, useEffect, useCallback, useMemo, useRef, useId, forwardRef, useImperativeHandle, type ReactNode } from 'react'
import { NODE_CARD_RADIUS_CLASS, isPortraitAspectRatio } from '../shared/constants'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { createPortal } from 'react-dom'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import * as Y from 'yjs'
import { Handle, Position, NodeProps, useNodes, useReactFlow } from 'reactflow'
import { type ByomModelInfo, type ExtractVideoAudioMode } from '../../../../api/client'
import { getAssetUrl, getThumbnailUrl, getVideoPosterUrl } from '../../../../api/useAssetUrl'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import { dataStatus,dataStrOpt, type ReactFlowCanvasEdgeData, type ReactFlowCanvasNode, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { createNadDebug } from '../../../../utils/debugLog'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { resolveVideoBanner, resolveVideoCardOverlay } from './videoBanner'
import { getVideoRunningPresentation } from './videoRunningPresentation'
import { VIDEO_GENERATOR_CONFIG_TAB_ID } from './bottomPanelTabs'
import { SlidersHorizontal, MessageSquarePlus, BookmarkPlus, Check, Loader2, ZoomIn, ZoomOut, Maximize2, Minimize2 } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { NodeDownloadPreparingBanner } from '../shared/NodeDownloadPreparingBanner'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { useIsPreparingC2paDownload } from '../shared/c2paDownloadProgress'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  useNodeActions,
  useSaveToLibrary,
  useBottomPanelAutoOpen,
  UnifiedBottomPanel,
  FlatIdleNodeCard,
  ReferenceSelector,
  CanvasRichTextBox,
  RichTextBox,
  CREATIVE_TEXT_MAX_LENGTH,
  logCreativeTextLimitConfigured,
  CameraCommandsPanel,
  VideoPreviewPlayer,
  useSelectSingleCanvasNode,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT,
  getCanvasNodeCardIntrinsicStyle,
  getPreviewIntrinsicHeight,
  snapNaturalDimensionsToDisplayDimensions,
  getPickedVideoIndex,
  type RichTextBoxHandle,
  type RichTextBoxState,
  type ToolbarAction,
  type TabDefinition,
  type VideoHistoryEntry,
  type SelectedReference,
  type SnappedDisplayDimensions,
  type VideoPreviewPlayerHandle,
  useDepthVideoAction,
  useExtractAudioAction,
  useExtractFrameAction,
  useUpscaleVideoAction,
  buildOrderedReferenceMedia,
  replaceReferenceMediaEdges,
  referenceMediaKey,
  buildReferenceMediaOrder,
  resolveCharacterOwnedAudioReferenceLabels,
  buildCharacterVoiceAudioReferences,
  mergeCharacterVoiceReferenceEdgeUpdates,
  buildCandidates,
  createHandlesFromSchema,
  QuickInfoBar,
  computeVariantPanelLayout,
  type CanvasAssetCandidate,
  type MediaType,
  type MentionCandidate,
} from '../shared'
import {
  videoDimensionsFromMetadata,
  type VideoDimensions,
} from '../shared/useUpscaleVideoAction'
import { localizeDefaultNodeName } from '../shared/defaultNodeNames'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { VideoConfigTabContent } from '../shared/VideoConfigTabContent'
import { VideoVariantsTabContent, resolveVideoGridAspect } from '../shared/VideoVariantsTabContent'
import { getReferenceAudioLimit, getReferenceSelectorTotalLimit, getReferenceVideoLimit, getVideoGeneratorReferenceLimits, getVideoGeneratorReferenceSelectorTabs, getVideoReferencePolicy, modeOnModelChange, showsFirstLastByOrderNotice, snapAspectRatioOnModelChange, snapOnModelChange, snapOnResolutionChange, isByomModel, effectiveVideoResolution } from '../shared/videoConstraints'
import { getVideoModelDisplayName } from '../shared/videoModelDisplayName'
import { DEFAULT_VIDEO_MODEL_ID } from '../../../../generated/modelCapabilities'
import { useVideoModels } from '../../../../hooks/useVideoModels'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { generateModeFor, modelSourceOf, trackVariantKeptChange } from '../../../../analytics/nodeAnalytics'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useGenerationGracePeriod } from '../../../../features/generationGracePeriod/useGenerationGracePeriod'
import {
  resolveVideoGeneratorAudioDisplaySettings,
} from '../../../../utils/videoGeneratorAudioDefaults'

import {
  NODE_SCHEMAS_BY_TYPE,
  getTypedNodeData,
  type DialogueEntryDict,
  type GeneratedVideo,
  type GeneratedVideoYMap,
  type ReactFlowCanvasNodeDataByType,
  type VoiceProfileDict,
  VoiceProfileDictYMap,
  bindCanvasNodeByFeType,
  type VideoReferenceEditDict,
} from '../../../../generated/nodeDataTypes'
import { buildClipDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { parseScript, serializeScript, type ScriptBlock } from './scriptParser'
import { parseMotionPrompt } from './motionPromptParser'
import { EMPTY_SPEAKER_NAME_INDEX, type SpeakerNameIndex } from './speakerToken'
import { useKnownSpeakerIndex } from './useKnownSpeakerIndex'
import {
  retiredVideoGeneratorAudioInputHandleOverrides,
  retiredVideoGeneratorAudioOutputHandleOverrides,
} from './videoGeneratorHandles'
import { ViewToggle, type ViewMode } from './scriptParts/ViewToggle'
import { MotionPromptExpandedModal } from './MotionPromptExpandedModal'
import { ScriptBlockView } from './scriptParts/ScriptBlockView'
import { ScriptBlockEditor } from './scriptParts/ScriptBlockEditor'
import { AddBlockButton } from './scriptParts/AddBlockButton'
import { focusEditableAtEnd, useCommitOnClickAway } from './scriptParts/useCommitOnClickAway'
import {
  TimeRuler,
  buildTimeScale,
  TIMELINE_ZOOM_LEVELS,
  DEFAULT_TIMELINE_ZOOM_INDEX,
  TIMELINE_SIDE_COLUMN_PX,
  TIMELINE_INSET_PX,
} from './scriptParts/TimeRuler'
import { VideoReferencePicker } from './VideoReferencePicker'
import { useTranslation } from 'react-i18next'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

type DialogueEntry = DialogueEntryDict
type VoiceProfile = VoiceProfileDict | VoiceProfileDictYMap | null
type ConcreteVoiceProfile = VoiceProfileDict | VoiceProfileDictYMap
type VideoGeneratorNodeData = ReactFlowCanvasNodeDataByType['videoGenerator']
const videoGeneratorSchema = NODE_SCHEMAS_BY_TYPE.videoGenerator
const HANDLES = createHandlesFromSchema(videoGeneratorSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_reference_media: { position: Position.Top, style: { left: '50%' } },
    in_images: { position: Position.Top, style: { left: '42%' } },
    in_reference_videos: { position: Position.Top, style: { left: '58%' } },
    in_audio: { style: { top: '20%' } },
    in_dialogue_prompt: { style: { top: '50%' } },
    in_voice_profiles: { style: { top: '85%' } },
    ...retiredVideoGeneratorAudioInputHandleOverrides,
  },
  outputs: {
    out_video: { style: { left: '50%' } },
    ...retiredVideoGeneratorAudioOutputHandleOverrides,
  },
})
type VideoGeneratorConfigUpdateKey =
  | 'in_name'
  | 'in_motion_prompt'
  | 'in_model'
  | 'in_style'
  | 'in_mode'
  | 'in_duration'
  | 'in_reference_video_edits'
  | 'in_resolution'
  | 'in_aspect_ratio'
  | 'in_provider_generate_audio'
  | 'in_use_voice_profile_audio_references'
  | 'in_audio_remix_mode'
  | 'in_allow_bgm'
type VideoGeneratorConfigUpdates = Partial<Pick<VideoGeneratorNodeData, VideoGeneratorConfigUpdateKey>>

function isConcreteVoiceProfile(profile: VoiceProfile): profile is ConcreteVoiceProfile {
  return profile !== null
}

function voiceProfileToJson(
  profile: ConcreteVoiceProfile | undefined | null,
): VoiceProfileDict | null {
  if (!profile) return null
  return profile instanceof VoiceProfileDictYMap ? profile.toJSON() : profile
}

function videoHistoryPath(entry: VideoHistoryEntry | undefined): string | undefined {
  const path = entry?.result?.local_path
  return typeof path === 'string' && path.length > 0 ? path : undefined
}

function generatedVideoHistoryToJson(
  history: GeneratedVideoYMap[] | undefined,
): GeneratedVideo[] {
  return (history ?? []).map((entry) => entry.toJSON() as GeneratedVideo)
}

const OMNI_REFERENCE_VIDEO_MAX_SECONDS = 10
const VIDEO_GENERATOR_VARIANTS_TAB_ID = 'variants'

function activeVideoGeneratorBottomPanelTab(tab: string): string {
  return tab === VIDEO_GENERATOR_VARIANTS_TAB_ID ? VIDEO_GENERATOR_VARIANTS_TAB_ID : VIDEO_GENERATOR_CONFIG_TAB_ID
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function maybeToJson(value: unknown): unknown {
  if (isRecord(value) && typeof value.toJSON === 'function') {
    return (value.toJSON as () => unknown)()
  }
  return value
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function videoReferenceEditToJson(value: unknown): VideoReferenceEditDict | null {
  const raw = maybeToJson(value)
  if (!isRecord(raw)) return null

  const edit: VideoReferenceEditDict = {}
  const sourceNodeId = raw.source_node_id
  const assetPath = raw.asset_path
  const startTime = finiteNumber(raw.start_time)
  const endTime = finiteNumber(raw.end_time)
  if (typeof sourceNodeId === 'string' && sourceNodeId) edit.source_node_id = sourceNodeId
  if (typeof assetPath === 'string' && assetPath) edit.asset_path = assetPath
  if (startTime !== undefined) edit.start_time = startTime
  if (endTime !== undefined) edit.end_time = endTime
  return edit
}

function videoReferenceEditsToJson(value: unknown): VideoReferenceEditDict[] {
  const raw = maybeToJson(value)
  if (!Array.isArray(raw)) return []
  return raw
    .map(videoReferenceEditToJson)
    .filter((edit): edit is VideoReferenceEditDict => edit !== null)
}

function findVideoReferenceEdit(
  edits: readonly VideoReferenceEditDict[],
  ref: Pick<SelectedReference, 'nodeId' | 'assetPath'>,
): VideoReferenceEditDict | undefined {
  return edits.find((edit) => edit.source_node_id === ref.nodeId || edit.asset_path === ref.assetPath)
}

/**
 * Default prefill window for a reference-video trim when the model declares no
 * per-clip ceiling. Only seeds the UI inputs — it is not a cap.
 */
const DEFAULT_REFERENCE_VIDEO_WINDOW_SECONDS = OMNI_REFERENCE_VIDEO_MAX_SECONDS

export function normalizedVideoReferenceEdit(
  ref: Pick<SelectedReference, 'nodeId' | 'assetPath'>,
  existing?: VideoReferenceEditDict,
  maxSeconds?: number | null,
): VideoReferenceEditDict {
  const startTime = finiteNumber(existing?.start_time) ?? 0
  const existingEnd = finiteNumber(existing?.end_time)
  const window = maxSeconds ?? DEFAULT_REFERENCE_VIDEO_WINDOW_SECONDS
  const endTime = existingEnd !== undefined && existingEnd > startTime
    ? existingEnd
    : startTime + window
  return {
    source_node_id: ref.nodeId,
    asset_path: ref.assetPath,
    start_time: startTime,
    end_time: endTime,
  }
}

export function syncVideoReferenceEdits(
  existingEdits: readonly VideoReferenceEditDict[],
  videoRefs: readonly Pick<SelectedReference, 'nodeId' | 'assetPath'>[],
  maxSeconds?: number | null,
): VideoReferenceEditDict[] {
  return videoRefs.map((ref) =>
    normalizedVideoReferenceEdit(ref, findVideoReferenceEdit(existingEdits, ref), maxSeconds),
  )
}

/**
 * Locale-neutral codes for the client-side validations that BLOCK generation on
 * a Video Generator node. Post 1586850233072274 promoted these from a `title`
 * tooltip on the (already disabled) generate button to the node's error banner,
 * so they must be localizable — hence codes resolved through `t()` at render
 * time rather than prose returned from these helpers.
 *
 * These are RENDER-TIME values derived from YDoc config; they are never
 * persisted into `data.error`, so they intentionally do NOT use the `dw-err:`
 * envelope from `nodeErrorCode.ts` (which exists for the persisted case). Each
 * code maps to `canvas:generation.blockReason.<code>` — the SHARED namespace
 * (RFC-1202 §4.5), so the identical code renders identically wherever it is
 * raised: this node, an image node, or the backend crossing REST.
 */
type VideoBlockReasonCode = BlockReason['code']

/**
 * A block reason: a locale-neutral code plus its interpolation params.
 *
 * The reference-video codes used to bake the model into the identifier
 * (`referencesRequireOmniFlash`, `omniFlashTrimTooLong`). They cannot any more:
 * WHICH model refuses a video reference, and what cap it imposes, is read from
 * that model's declared capability — so the model name and the number are data,
 * not part of the key.
 */
export interface VideoBlockReason {
  code: VideoBlockReasonCode
  params?: Record<string, string | number>
}

/**
 * Does the COMBINED reference count exceed the model's total cap?
 *
 * Separate from the per-media caps: Seedance 2.0 allows 9 images AND 3 audio
 * but only 12 references overall, so a selection legal on every individual axis
 * can still be refused.
 *
 * This guards a case the selector cannot: `maxReferences` only constrains
 * FUTURE picks inside the modal. A node that already holds N references — from
 * a model switch, an older canvas, or Director wiring — keeps them, so the
 * Generate gate has to re-check what is actually attached.
 *
 * `null` means the model declares no total (BYOM), which is not zero.
 */
export function exceedsTotalReferenceCap(
  totalReferences: number,
  maxTotal: number | null,
): boolean {
  if (maxTotal === null) return false
  return totalReferences > maxTotal
}

/**
 * Does the SUM of trim windows exceed a model's combined ceiling?
 *
 * Split out from getReferenceVideoBlockReason so the arithmetic and its
 * boundary are testable on their own. They otherwise could not be: the rule
 * only fires for a model declaring BOTH multiple videos and a combined cap.
 * Seedance 2.0 R2V is the native contract that exercises this rule.
 *
 * `null` means the model declares no total, which is NOT the same as zero.
 */
export function exceedsCombinedTrimCeiling(
  windowSeconds: readonly number[],
  maxCombined: number | null,
): boolean {
  if (maxCombined === null) return false
  const total = windowSeconds.reduce((sum, seconds) => sum + seconds, 0)
  return total > maxCombined
}

/**
 * Why Generate is blocked for the currently linked reference videos, if it is.
 *
 * Returns a LOCALE-NEUTRAL code plus interpolation params, not prose. Post
 * 1586850233072274 promoted these reasons from a `title` tooltip to the node's
 * error banner, so they must be localizable; and because the rule is now
 * model-capability driven rather than "is it Omni?", the copy has to name the
 * model the user actually picked and that model's real cap. Codes are resolved
 * through `t()` at render time — see `canvas:generation.blockReason.*`.
 *
 * These are RENDER-TIME values derived from YDoc config; they are never
 * persisted into `data.error`, so they intentionally do NOT use the `dw-err:`
 * envelope from `nodeErrorCode.ts` (which exists for the persisted case).
 *
 * Exported for direct unit testing: this is the whole of feature 2 ("submit
 * fails when a video is linked but the model does not support it"). Mirrors the
 * backend `video_reference_policy.reference_video_rejection`; the two must
 * agree on WHEN a reference is refused even though the copy differs.
 */
export function getReferenceVideoBlockReason(
  model: string,
  videoRefs: readonly Pick<SelectedReference, 'nodeId' | 'assetPath'>[],
  edits: readonly VideoReferenceEditDict[],
  modelLabel: string,
  capabilities?: Record<string, string[] | string> | null,
): VideoBlockReason | undefined {
  if (videoRefs.length === 0) return undefined

  const policy = getVideoReferencePolicy(model, capabilities)
  const maxSeconds = policy.maxVideoSecondsEach
  const windowSeconds: number[] = []
  for (const ref of videoRefs) {
    // Validate what the USER actually stored, not a normalized copy.
    // normalizedVideoReferenceEdit REPAIRS a missing-or-invalid end time
    // (start + window), so validating its output made the inverted-window check
    // dead code: the console reported "ready to generate" while the backend's
    // _prepare_reference_videos, which reads the raw in_reference_video_edits,
    // rejected the same node with "trim window does not overlap".
    // Use normalized fallback values only for missing members of an existing
    // legacy edit record.
    const stored = findVideoReferenceEdit(edits, ref)
    // Current selections no longer author trim windows, and the selector does
    // not know source duration. The backend probes the real clips before
    // provider submission; do not invent a duration here.
    if (!stored) continue
    const seeded = normalizedVideoReferenceEdit(ref, stored, maxSeconds)
    const startTime = finiteNumber(stored?.start_time) ?? finiteNumber(seeded.start_time)
    const endTime = finiteNumber(stored?.end_time) ?? finiteNumber(seeded.end_time)
    if (startTime === undefined || endTime === undefined || startTime < 0 || endTime <= startTime) {
      return { code: 'referenceTrimOrder' }
    }
    windowSeconds.push(endTime - startTime)
  }

  return preflight({
    modelId: model, phase: 'preview', byomCapabilities: capabilities,
    inputCounts: { in_images: null, in_reference_videos: videoRefs.length, in_reference_audios: null },
    inputSeconds: { in_reference_videos: windowSeconds },
  }, modelLabel, "console-video-trim")[0]

}

/** Warn without blocking when linked character voices cannot condition the model. */
export function shouldWarnCharacterVoiceReferenceUnsupported(
  activeCharacterVoiceCount: number,
  model: string,
  capabilities?: Record<string, string[] | string> | null,
): boolean {
  return (
    activeCharacterVoiceCount > 0 &&
    getReferenceAudioLimit(model, capabilities) === 0
  )
}

/**
 * Total-reference fallback for unknown native models without a generated
 * policy.
 *
 * Historically a literal 9 inline on ReferenceSelector. Known models with an
 * explicit null policy remain uncapped; documented numeric caps pass through.
 */
const DEFAULT_REFERENCE_TOTAL_LIMIT = 9
const CHARACTER_VOICE_SOURCE_OWNER_FIELD = 'in_character_voice_source_for'

function hasVoiceBrief(profile: VoiceProfileDict | VoiceProfileDictYMap | null | undefined): boolean {
  const raw = voiceProfileToJson(profile)
  return Boolean(
    raw?.name?.trim() ||
    raw?.description?.trim() ||
    raw?.voice_prompt?.trim() ||
    raw?.voice_name?.trim(),
  )
}

function linkedCharacterVoiceSoundNodeId(characterNodeId: string): string {
  return `${characterNodeId}__voice_sound`
}

function isActiveCharacterVoiceSourceNode(
  characterNodeId: string,
  node: ReactFlowCanvasNode | undefined,
): boolean {
  if (node?.type === 'audio') return true
  if (node?.type !== 'soundExpert') return false
  if (node.id === linkedCharacterVoiceSoundNodeId(characterNodeId)) return true
  const soundData = getTypedNodeData(node, 'soundExpert')
  return soundData?.[CHARACTER_VOICE_SOURCE_OWNER_FIELD] === characterNodeId
}

function activeCharacterVoiceSourceNodes(
  characterNodeId: string,
  nodesById: Map<string, ReactFlowCanvasNode>,
  edges: ReturnType<typeof useCanvasEdges>,
): ReactFlowCanvasNode[] {
  const results: ReactFlowCanvasNode[] = []
  const seen = new Set<string>()
  const add = (nodeId: string) => {
    if (seen.has(nodeId)) return
    const node = nodesById.get(nodeId)
    if (!node || !isActiveCharacterVoiceSourceNode(characterNodeId, node)) return
    seen.add(nodeId)
    results.push(node)
  }

  for (const edge of edges) {
    if (
      edge.source === characterNodeId &&
      edge.sourceHandle === 'out_voice_profile' &&
      edge.targetHandle === 'in_voice_profile'
    ) {
      add(edge.target)
    }
  }
  if (results.length === 0) add(linkedCharacterVoiceSoundNodeId(characterNodeId))
  return results
}

function characterReferencesWithActiveVoice(
  refs: SelectedReference[],
  nodes: ReactFlowCanvasNode[],
  edges: ReturnType<typeof useCanvasEdges>,
): Array<Pick<SelectedReference, 'nodeId' | 'nodeType' | 'sourceHandle' | 'fromIndex'>> {
  const nodesById = new Map(nodes.map((candidate) => [candidate.id, candidate]))
  const voiceSources: Array<Pick<SelectedReference, 'nodeId' | 'nodeType' | 'sourceHandle' | 'fromIndex'>> = []
  for (const ref of refs) {
    if (ref.nodeType !== 'characterDesigner') continue
    const sourceNode = nodesById.get(ref.nodeId)
    if (!sourceNode || sourceNode.type !== 'characterDesigner') continue

    let hasSourceProfile = false
    for (const voiceNode of activeCharacterVoiceSourceNodes(ref.nodeId, nodesById, edges)) {
      if (
        voiceNode.type === 'soundExpert' &&
        hasVoiceBrief(getTypedNodeData(voiceNode, 'soundExpert')?.out_voice_profile)
      ) {
        voiceSources.push({
          nodeId: voiceNode.id,
          nodeType: 'soundExpert',
          sourceHandle: 'out_voice_profile',
        })
        hasSourceProfile = true
        break
      }
    }
    if (hasSourceProfile) {
      continue
    }

    const profile = getTypedNodeData(sourceNode, 'characterDesigner')?.out_voice_profile
    if (hasVoiceBrief(profile)) {
      voiceSources.push({
        nodeId: ref.nodeId,
        nodeType: 'characterDesigner',
        sourceHandle: 'out_voice_profile',
      })
    }
  }
  return voiceSources
}

function isVideoGeneratorReferenceCandidate(candidate: CanvasAssetCandidate): boolean {
  return candidate.mediaType !== 'audio' || candidate.sourceHandle === 'out_audio'
}

/* ── Debug logging ── */
const videoStyleLog = createNadDebug('dw:video-style')
const voiceProfileSourceLog = createNadDebug('dw:voice-profile-source:data')
const avScriptLog = createNadDebug('dw:av-script-format')

const clipLabelLog = createNadDebug('dw:video-clip-label')
const videoVariantSyncLog = createNadDebug('dw:video-variant-sync')
const variantsPanelLog = createNadDebug('dw:variants-panel')
const canvasNodeIntrinsicSizeLog = createNadDebug('dw:canvas-node-intrinsic-size')
const videoConfigDispatchGuardLog = createNadDebug('dw:video-config-dispatch-guard')
const videoGenerationWaitHintLog = createNadDebug('dw:video-generation-wait-hint')
const videoModelValidationLog = createNadDebug('dw:video-model-validation:node')
// Post 1586850233072274: trace which message the node's error banner resolves
// to, so a reviewer can see a stale error being superseded by the current,
// actionable block reason instead of inferring it from the DOM.
const videoBlockReasonBannerLog = createNadDebug('dw:video-generate-block-reason')
const referenceMediaOrderLog = createNadDebug('dw:reference-media-order')
const videoReferenceEditingLog = createNadDebug('dw:video-reference-editing')
const videoAudioReferenceVisibilityLog = createNadDebug('dw:video-audio-reference-visibility')
const motionPromptReferenceLog = createNadDebug('dw:canvas:motion-prompt-references')
const motionPromptSaveLog = createNadDebug('dw:canvas:motion-prompt-save')
const previewHoverLog = createNadDebug('dw:canvas:video-preview-hover')
// T276953451: config writes must go through the YDoc-first canvas path only.
// Writing to ReactFlow's internal store (useReactFlow().setNodes) in a
// controlled canvas emits a `reset` change that can re-insert a just-deleted
// node — the video-generator delete flicker. This trace proves the write is
// YDoc-first.
const videoConfigWriteLog = createNadDebug('dw:video-config-write:ydoc-first')

function logMotionPromptSave(nodeId: string, trigger: string, value: string): void {
  if (!motionPromptSaveLog.enabled) return
  motionPromptSaveLog({
    event: 'motion-prompt-save',
    trace_id: `motion-prompt-save:${nodeId}`,
    node_id: nodeId,
    trigger,
    text_length: value.length,
    newline_count: value.split('\n').length - 1,
  })
}
const videoModelCatalogLog = createNadDebug('dw:video-model-catalog:selection')

function isSelectableVideoModel(model: string, nativeModels: string[], byomModels: ByomModelInfo[]): boolean {
  if (nativeModels.includes(model)) return true
  if (!isByomModel(model)) return false
  return byomModels.some((entry) => entry.id === model && entry.enabled !== false)
}

function unavailableVideoModelReason(_model: string, t: (key: string) => string): string {
  return t('videoGeneratorNode.unavailableModelReason')
}
const VIDEO_CARD_HORIZONTAL_BORDER_PX = 2

/**
 * Video Generator config-panel tabs. Variants is intentionally NOT a tab — it
 * opens as a dedicated, tab-less panel via the QuickInfo bar (post
 * 1533007285123236), so variants zoom with the canvas while config stays
 * inverse-zoomed. Timeline audio editing belongs in Video Editor.
 */
const useVideoTabs = (): TabDefinition[] => {
  const { t } = useTranslation('canvas')
  return useMemo((): TabDefinition[] => [
    { id: 'config', label: t('videoGeneratorNode.tabVideoConfig'), icon: SlidersHorizontal },
  ], [t])
}

function formatLineCount(count: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t('videoGeneratorNode.lineCount', { count })
}

function getDialogueEntryPreview(entry: DialogueEntry, t: (key: string) => string): { time: string; speaker: string; text: string; type: string } {
  const start = entry.start_s ?? 0
  const end = entry.end_s ?? 0
  const speaker = entry.voice_character || t('videoGeneratorNode.speakerDefault')
  const { text } = splitVoiceText(entry.voice_text ?? '')
  const type = entry.type === 'voiceover' ? t('videoGeneratorNode.narratorType') : t('videoGeneratorNode.dialogueType')
  return {
    time: `${formatPromptTime(start)}s-${formatPromptTime(end)}s`,
    speaker,
    text,
    type,
  }
}

function splitVoiceText(voiceText: string): { text: string; emotion: string | null } {
  const match = voiceText.match(/^\[([^\]]+)\]\s*/)
  if (!match) return { text: voiceText, emotion: null }
  return {
    text: voiceText.slice(match[0].length).trim(),
    emotion: match[1].trim(),
  }
}

/* ── Render @[[nodeId]] markers as inline pill chips ── */

function renderTextWithChips(
  text: string,
  candidatesMap: Map<string, MentionCandidate>,
  projectId: string,
): ReactNode[] {
  const MARKER_RE = /@\[\[([^\]]+)\]\]/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIdx = 0

  while ((match = MARKER_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const nodeId = match[1]
    const candidate = candidatesMap.get(nodeId)

    if (candidate) {
      const thumbUrl = candidate.thumbnailPath
        ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
        : null

      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary align-baseline mx-0.5"
          data-testid={`mention-chip-${nodeId}`}
        >
          {thumbUrl && (
            <img
              src={thumbUrl}
              alt=""
              className="w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          )}
          <span className="inline-flex items-center gap-0.5">
            {NODE_TYPE_ICON[candidate.type] ? (
              <span className="w-3 h-3 text-current opacity-60 flex-shrink-0">{NODE_TYPE_ICON[candidate.type]}</span>
            ) : (
              <span className="text-xs">{candidate.icon}</span>
            )}
            <span>{candidate.label}</span>
          </span>
        </span>
      )
    } else {
      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs font-medium text-foreground/40 align-baseline mx-0.5 line-through"
          data-testid={`mention-chip-missing-${nodeId}`}
        >
          <span className="inline-flex items-center gap-0.5">
            <span>{nodeId.slice(0, 8)}...</span>
          </span>
        </span>
      )
    }

    keyIdx++
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  if (parts.length === 0) {
    parts.push(text)
  }

  return parts
}

/* ── Formatted Motion Prompt Display ── */

/** True when the wheel event started inside an element between `target` and
 * `boundary` that scrolls vertically (note text, an open editor's body). */
function wheelStartsInVerticalScroller(target: EventTarget, boundary: HTMLElement): boolean {
  let el = target instanceof Element ? target : null
  while (el && el !== boundary) {
    if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight) {
      const overflowY = window.getComputedStyle(el).overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') return true
    }
    el = el.parentElement
  }
  return false
}

/** In-place editor for a ClipWideNote. Saves on click-away (only when the
 * text changed); Escape discards. */
function ClipWideNoteEditor({
  text,
  testId,
  canvasNodes,
  onCommit,
  onClose,
}: {
  text: string
  testId: string
  canvasNodes: ReactFlowCanvasNode[]
  onCommit: (newText: string) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState(text)
  const rootRef = useRef<HTMLDivElement>(null)
  useCommitOnClickAway(rootRef, () => {
    if (draft.trim() !== text.trim()) onCommit(draft)
    onClose()
  })
  useEffect(() => {
    focusEditableAtEnd(rootRef.current)
  }, [])
  return (
    // Fills the card's remaining height; long text scrolls inside the box.
    <div ref={rootRef} className="flex-1 min-h-0 flex flex-col">
      <RichTextBox
        flexFill
        canvasNodes={canvasNodes}
        initialContent={text}
        onContentChange={(state: RichTextBoxState) => setDraft(state.plainText)}
        onEscape={onClose}
        containerClassName="w-full min-w-0 bg-input-bg border border-input-border rounded px-1.5 py-1 nowheel"
        containerTestId={`${testId}-editor`}
        className="overscroll-contain"
        maxLength={CREATIVE_TEXT_MAX_LENGTH}
      />
    </div>
  )
}

/** Strip for clip-wide text that is not a timestamped segment — the preamble
 * before the first timestamp and any trailing block after the last one. This
 * content is displayed rather than hidden so a lock or a `<voices>` roster
 * never looks deleted. Long text scrolls vertically inside the card. Clicking the text edits it in
 * place, inside this card; clicking away saves. */
function ClipWideNote({
  label,
  text,
  testId,
  onCommit,
  editLabel,
  candidatesMap,
  canvasNodes,
  projectId,
  sizeClassName,
}: {
  label: string
  text: string
  testId: string
  /** Height sizing within the notes column (flex column). */
  sizeClassName: string
  onCommit: (newText: string) => void
  editLabel: string
  candidatesMap: Map<string, MentionCandidate>
  canvasNodes: ReactFlowCanvasNode[]
  projectId: string
}) {
  const [editing, setEditing] = useState(false)
  return (
    <div
      className={`relative min-h-0 flex flex-col bg-surface-1 border border-border-subtle rounded-lg p-2.5 ${sizeClassName}`}
      data-testid={testId}
    >
      <div className="mb-1 shrink-0">
        <span className="text-xxs uppercase tracking-wider text-primary">{label}</span>
      </div>
      {editing ? (
        <ClipWideNoteEditor
          text={text}
          testId={testId}
          canvasNodes={canvasNodes}
          onCommit={onCommit}
          onClose={() => setEditing(false)}
        />
      ) : (
        // Single-click the text to edit it (I-beam cursor on hover). Long
        // text scrolls vertically inside the card.
        <p
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setEditing(true)
          }}
          title={editLabel}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain cursor-text text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid={`${testId}-edit-btn`}
        >
          {renderTextWithChips(text, candidatesMap, projectId)}
        </p>
      )}
    </div>
  )
}

interface FormattedMotionPromptProps {
  text: string
  nodeId: string
  onEdit: () => void
  onCommitStructured: (newText: string) => void
  projectId: string
  /**
   * Fires `true` whenever a structured per-block editor opens, `false` when
   * all per-block editors close. The parent ORs this with its own freeform
   * `isEditingPrompt` flag to decide whether to render the
   * CINEMATOGRAPHY PRESETS panel at the bottom of the Config tab.
   */
  onEditingChange?: (isEditing: boolean) => void
  /**
   * When true, the prompt body grows to fill the available vertical space
   * (flex-1 inside a bordered box) so the Config panel matches the Image
   * Generator's filling prompt textarea instead of leaving a void below a
   * compact display. The "Motion Prompt" label + view toggle stay pinned at
   * the top; only the body fills.
   */
  fill?: boolean
  /**
   * Connected voice-profile names, so a comma-bearing speaker in the prompt
   * resolves to the real profile instead of being truncated at the first comma.
   * Referentially stable (see useKnownSpeakerIndex) so `memo()` still holds.
   */
  knownSpeakerIndex?: SpeakerNameIndex
  /**
   * Whether the motion prompt is currently shown in the large expanded popup.
   * Drives the expand / collapse button beside the view toggle.
   */
  expanded?: boolean
  /** Toggles the expanded popup. When omitted, no expand button renders. */
  onToggleExpanded?: () => void
}

/**
 * Imperative handle exposed by `FormattedMotionPrompt` so the parent panel
 * can drive preset insertion in Structured mode (round 4 item 4):
 *  - `getActiveBodyRef()` — returns the open block editor's body
 *    RichTextBoxHandle if one is open, else null.
 *  - `appendVideoBlockFromPreset(preset)` — appends a new video block whose
 *    body is the preset text, then auto-opens the editor on that block.
 */
export interface FormattedMotionPromptHandle {
  getActiveBodyRef: () => RichTextBoxHandle | null
  appendVideoBlockFromPreset: (preset: string) => void
}

/** Width of the clip-wide notes column at the left of the motion timeline. */
const NOTES_COLUMN_PX = 280

/** Share of the timeline height given to the Video track; the user can drag
 * the divider between the Video and Audio tracks to change it. Persisted as a
 * per-browser preference, like the chat sidebar's layout. */
const DEFAULT_VIDEO_TRACK_FRACTION = 0.6
const VIDEO_TRACK_FRACTION_STORAGE_KEY = 'motion-prompt-video-track-fraction'

function readStoredVideoTrackFraction(): number {
  try {
    const stored = Number(localStorage.getItem(VIDEO_TRACK_FRACTION_STORAGE_KEY))
    if (Number.isFinite(stored) && stored > 0 && stored < 1) return stored
  } catch {
    /* storage unavailable — fall back to the default */
  }
  return DEFAULT_VIDEO_TRACK_FRACTION
}

const FormattedMotionPrompt = memo(
  forwardRef<FormattedMotionPromptHandle, FormattedMotionPromptProps>(
    function FormattedMotionPrompt(
      {
        text,
        nodeId,
        onEdit,
        onCommitStructured,
        projectId,
        onEditingChange,
        fill,
        knownSpeakerIndex = EMPTY_SPEAKER_NAME_INDEX,
        expanded = false,
        onToggleExpanded,
      },
      ref,
    ) {
  const { t } = useTranslation('canvas')
  const { emitNuxEvent } = useNuxTooltipEvents()
  const canvasNodes = useNodes<ReactFlowCanvasNodeData>()
  const parsed = useMemo(
    () => parseMotionPrompt(text, knownSpeakerIndex),
    [text, knownSpeakerIndex],
  )
  const structured = useMemo(
    () => parseScript(text, knownSpeakerIndex),
    [text, knownSpeakerIndex],
  )

  const overlapById = useMemo(() => {
    const set = new Set<string>()
    for (const [a, b] of structured.videoOverlap) {
      set.add(a)
      set.add(b)
    }
    return set
  }, [structured.videoOverlap])

  // Log parse outcome on every text change.
  useEffect(() => {
    avScriptLog({
      event: 'parse-done',
      nodeId,
      textLen: text.length,
      videoCount: structured.videoBlocks.length,
      audioCount: structured.audioBlocks.length,
      hasBgm: structured.disableReason?.reason === 'bgm-present',
      hasPreamble: structured.globalPreamble !== null,
      preambleLineCount: structured.globalPreamble?.split(/\r?\n/).length ?? 0,
      hasTrailing: structured.trailingText !== null,
      hasPreviousShotContext: structured.previousShotContext !== null,
      videoOverlap: structured.videoOverlap.length,
      audioOverlapCount: structured.audioOverlapCount,
      canStructured: structured.canStructured,
      disableReason: structured.disableReason?.reason ?? null,
    })
  }, [nodeId, text, structured])

  // PREVIOUS SHOT CONTEXT describes the prior clip's ending state and applies to
  // the whole clip, so it is shown with the preamble, not in the last segment.
  const clipWideNotesText =
    [structured.globalPreamble, structured.previousShotContext].filter(Boolean).join('\n\n') ||
    null

  const [mode, setMode] = useState<ViewMode>(structured.canStructured ? 'structured' : 'freeform')
  const [editingId, setEditingId] = useState<string | null>(null)
  // Round 4 item 4: forward the open block-editor's body RichTextBox handle
  // up to the parent so CINEMATOGRAPHY PRESETS clicks can target the caret.
  const activeBodyRef = useRef<RichTextBoxHandle>(null)
  const [draftBlock, setDraftBlock] = useState<{
    track: 'video' | 'audio'
    block: ScriptBlock
  } | null>(null)

  // Notify parent whenever per-block editing toggles so the parent can keep
  // the CINEMATOGRAPHY PRESETS panel visible while ANY editor (structured or
  // freeform) is active. We only fire on transitions to avoid a render loop.
  useEffect(() => {
    if (!onEditingChange) return
    onEditingChange(editingId !== null)
  }, [editingId, onEditingChange])

  // Auto-fallback to freeform when structured becomes unavailable.
  useEffect(() => {
    if (!structured.canStructured && mode === 'structured') {
      setMode('freeform')
    }
  }, [structured.canStructured, mode])

  // Generate disabled log fires whenever overlap appears.
  useEffect(() => {
    if (structured.videoOverlap.length > 0) {
      avScriptLog({
        event: 'generate-disabled',
        nodeId,
        reason: 'video-overlap',
        overlapPair: structured.videoOverlap[0],
      })
    }
  }, [nodeId, structured.videoOverlap])

  const candidatesMap = useMemo(() => {
    const candidates = buildCandidates(canvasNodes)
    return new Map(candidates.map(c => [c.id, c]))
  }, [canvasNodes])
  const lastMotionPromptReferenceStateRef = useRef<string | null>(null)

  useEffect(() => {
    if (!motionPromptReferenceLog.enabled) return
    const referencedNodeIds = Array.from(
      new Set(Array.from(text.matchAll(/@\[\[([^\]]+)\]\]/g), (match) => match[1])),
    )
    const missingNodeIds = referencedNodeIds.filter((referencedId) => !candidatesMap.has(referencedId))
    const referencedIds = referencedNodeIds.join(',')
    const missingIds = missingNodeIds.join(',')
    const state = [canvasNodes.length, candidatesMap.size, referencedIds, missingIds].join('\u0000')
    if (lastMotionPromptReferenceStateRef.current === state) return
    lastMotionPromptReferenceStateRef.current = state
    motionPromptReferenceLog({
      event: 'motion-prompt-reference-resolution',
      node_id: nodeId,
      canvas_node_count: canvasNodes.length,
      candidate_count: candidatesMap.size,
      referenced_ids: referencedIds,
      missing_ids: missingIds,
    })
  }, [candidatesMap, canvasNodes.length, nodeId, text])

  const commitBlocks = useCallback(
    (videoBlocks: ScriptBlock[], audioBlocks: ScriptBlock[]) => {
      const newText = serializeScript({
        clipHeader: structured.clipHeader,
        globalPreamble: structured.globalPreamble,
        trailingText: structured.trailingText,
        previousShotContext: structured.previousShotContext,
        videoBlocks,
        audioBlocks,
      })
      avScriptLog({
        event: 'serialize-commit',
        nodeId,
        blocksCount: videoBlocks.length + audioBlocks.length,
        textLen: newText.length,
        hasPreamble: structured.globalPreamble !== null,
        hasTrailing: structured.trailingText !== null,
        hasPreviousShotContext: structured.previousShotContext !== null,
      })
      onCommitStructured(newText)
    },
    [
      structured.clipHeader,
      structured.globalPreamble,
      structured.trailingText,
      structured.previousShotContext,
      nodeId,
      onCommitStructured,
    ],
  )

  // Clip-wide note edits. PREVIOUS SHOT CONTEXT is displayed (and edited)
  // inside the clip-wide notes, so the edited text replaces both the preamble
  // and that paragraph; serializeScript writes them back-to-back anyway, and
  // the parser re-extracts the paragraph on the next parse.
  const commitNotes = useCallback(
    (which: 'preamble' | 'trailing', editedText: string) => {
      const value = editedText.trim() || null
      const newText = serializeScript({
        clipHeader: structured.clipHeader,
        globalPreamble: which === 'preamble' ? value : structured.globalPreamble,
        previousShotContext: which === 'preamble' ? null : structured.previousShotContext,
        trailingText: which === 'trailing' ? value : structured.trailingText,
        videoBlocks: structured.videoBlocks,
        audioBlocks: structured.audioBlocks,
      })
      avScriptLog({
        event: 'notes-commit',
        nodeId,
        which,
        notesLen: value?.length ?? 0,
        textLen: newText.length,
      })
      onCommitStructured(newText)
    },
    [structured, nodeId, onCommitStructured],
  )

  const handleSaveBlock = useCallback(
    (updated: ScriptBlock) => {
      const draft = draftBlock?.block.id === updated.id ? draftBlock : null
      if (draft) {
        const nextVideo =
          draft.track === 'video' ? [...structured.videoBlocks, updated] : structured.videoBlocks
        const nextAudio =
          draft.track === 'audio' ? [...structured.audioBlocks, updated] : structured.audioBlocks
        avScriptLog({
          event: 'block-add-draft-saved',
          nodeId,
          blockId: updated.id,
          track: draft.track,
          kind: updated.kind,
          blocksCountAfter: nextVideo.length + nextAudio.length,
          textLen: updated.text.length,
        })
        setDraftBlock(null)
        setEditingId(null)
        commitBlocks(nextVideo, nextAudio)
        return
      }

      const inVideo = structured.videoBlocks.some((b) => b.id === updated.id)
      const inAudio = structured.audioBlocks.some((b) => b.id === updated.id)
      const nextVideo = inVideo
        ? structured.videoBlocks.map((b) => (b.id === updated.id ? updated : b))
        : structured.videoBlocks
      const nextAudio = inAudio
        ? structured.audioBlocks.map((b) => (b.id === updated.id ? updated : b))
        : structured.audioBlocks
      avScriptLog({
        event: 'block-edit',
        nodeId,
        blockId: updated.id,
        kind: updated.kind,
        startSec: updated.startSec,
        endSec: updated.endSec,
        textLen: updated.text.length,
      })
      if (draftBlock) {
        setDraftBlock(null)
      }
      setEditingId(null)
      commitBlocks(nextVideo, nextAudio)
    },
    [draftBlock, structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleCancelBlockEdit = useCallback(
    (blockId: string) => {
      if (draftBlock?.block.id === blockId) {
        avScriptLog({
          event: 'block-add-draft-cancelled',
          nodeId,
          blockId,
          track: draftBlock.track,
          committed: false,
        })
        setDraftBlock(null)
      }
      setEditingId(null)
    },
    [draftBlock, nodeId],
  )

  const handleEditBlock = useCallback(
    (blockId: string) => {
      if (draftBlock && draftBlock.block.id !== blockId) {
        avScriptLog({
          event: 'block-add-draft-discarded',
          nodeId,
          blockId: draftBlock.block.id,
          track: draftBlock.track,
          reason: 'edit-other-block',
        })
        setDraftBlock(null)
      }
      setEditingId(blockId)
    },
    [draftBlock, nodeId],
  )

  const handleDeleteBlock = useCallback(
    (blockId: string, track: 'video' | 'audio') => {
      if (draftBlock) {
        const deletingDraft = draftBlock.block.id === blockId
        avScriptLog({
          event: 'block-add-draft-discarded',
          nodeId,
          blockId: draftBlock.block.id,
          track: draftBlock.track,
          reason: deletingDraft ? 'delete-draft-block' : 'delete-other-block',
        })
        setDraftBlock(null)
        if (editingId === draftBlock.block.id) {
          setEditingId(null)
        }
        if (deletingDraft) return
      }

      const kind =
        (track === 'video' ? structured.videoBlocks : structured.audioBlocks).find(
          (b) => b.id === blockId,
        )?.kind ?? 'video'
      avScriptLog({ event: 'block-delete', nodeId, blockId, kind })
      const nextVideo =
        track === 'video'
          ? structured.videoBlocks.filter((b) => b.id !== blockId)
          : structured.videoBlocks
      const nextAudio =
        track === 'audio'
          ? structured.audioBlocks.filter((b) => b.id !== blockId)
          : structured.audioBlocks
      commitBlocks(nextVideo, nextAudio)
    },
    [draftBlock, editingId, structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleTimestampCommit = useCallback(
    (blockId: string, track: 'video' | 'audio', startSec: number, endSec: number) => {
      const update = (blocks: ScriptBlock[]) =>
        blocks.map((b) => (b.id === blockId ? { ...b, startSec, endSec } : b))
      const nextVideo = track === 'video' ? update(structured.videoBlocks) : structured.videoBlocks
      const nextAudio = track === 'audio' ? update(structured.audioBlocks) : structured.audioBlocks
      avScriptLog({
        event: 'block-edit',
        nodeId,
        blockId,
        kind: 'timestamp',
        startSec,
        endSec,
        textLen: 0,
      })
      commitBlocks(nextVideo, nextAudio)
    },
    [structured.videoBlocks, structured.audioBlocks, nodeId, commitBlocks],
  )

  const handleAddBlock = useCallback(
    (track: 'video' | 'audio', initialText = '') => {
      if (draftBlock) {
        avScriptLog({
          event: 'block-add-draft-ignored',
          nodeId,
          blockId: draftBlock.block.id,
          track: draftBlock.track,
          requestedTrack: track,
        })
        return
      }

      const target = track === 'video' ? structured.videoBlocks : structured.audioBlocks
      const lastEnd = target.reduce((m, b) => Math.max(m, b.endSec), 0)
      const startSec = lastEnd
      const endSec = lastEnd + 1
      // Use crypto.randomUUID when available so the new block has a stable id.
      const id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `blk-new-${Date.now()}`
      const block: ScriptBlock =
        track === 'video'
          ? { id, kind: 'video', startSec, endSec, text: initialText }
          : { id, kind: 'dialogue', startSec, endSec, text: initialText, character: '', emotion: '' }
      avScriptLog({ event: 'block-add', nodeId, kind: block.kind })
      avScriptLog({
        event: 'block-add-draft-opened',
        nodeId,
        blockId: id,
        track,
        kind: block.kind,
        startSec,
        endSec,
        textLen: initialText.length,
        existingBlockCount: target.length,
      })
      setDraftBlock({ track, block })
      setEditingId(id)
    },
    [draftBlock, structured.videoBlocks, structured.audioBlocks, nodeId],
  )

  // Round 4 item 4: expose imperative handle so the parent can route
  // CINEMATOGRAPHY PRESETS clicks into either the open block editor's
  // RichTextBox (preferred) or a new video block when no editor is open.
  useImperativeHandle(
    ref,
    () => ({
      getActiveBodyRef: () => (editingId !== null ? activeBodyRef.current : null),
      appendVideoBlockFromPreset: (preset: string) => {
        // Insert `[Preset]` as the body of a brand-new video block, then
        // auto-open the local draft editor.
        handleAddBlock('video', `[${preset}] `)
      },
    }),
    [editingId, handleAddBlock],
  )

  const handleModeChange = useCallback(
    (next: ViewMode) => {
      avScriptLog({
        event: 'mode-toggle',
        nodeId,
        from: mode,
        to: next,
        canStructured: structured.canStructured,
      })
      setMode(next)
      emitNuxEvent({
        type: 'controlActivated',
        controlId: next === 'structured' ? 'structuredModeSelected' : 'freeformModeSelected',
        anchorId: 'video-generator-modes',
        nodeId,
        nodeType: 'videoGenerator',
      })
    },
    [emitNuxEvent, mode, nodeId, structured.canStructured],
  )

  // Round 4 item 2: revert the round-3 in-flow editor approach for the EDIT
  // path. When `editingId === block.id`, the block's own absolute slot now
  // swaps to the ScriptBlockEditor in the same position. PRESETS overlap is
  // avoided via container `min-height` (audio/video columns flex to fit the
  // editor's natural height — `renderColumnContainer` below).

  const renderVideoBlocks = useMemo(
    () =>
      draftBlock?.track === 'video'
        ? [...structured.videoBlocks, draftBlock.block]
        : structured.videoBlocks,
    [draftBlock, structured.videoBlocks],
  )

  const renderAudioBlocks = useMemo(
    () =>
      draftBlock?.track === 'audio'
        ? [...structured.audioBlocks, draftBlock.block]
        : structured.audioBlocks,
    [draftBlock, structured.audioBlocks],
  )

  const videoColumns: ScriptBlock[][] = useMemo(() => {
    if (structured.videoOverlap.length === 0) {
      return [renderVideoBlocks]
    }
    const cols: ScriptBlock[][] = [[], []]
    const colEnds = [0, 0]
    for (const b of renderVideoBlocks) {
      let placed = false
      for (let i = 0; i < cols.length; i++) {
        if (b.startSec >= colEnds[i]) {
          cols[i].push(b)
          colEnds[i] = b.endSec
          placed = true
          break
        }
      }
      if (!placed) {
        cols[1].push(b)
        colEnds[1] = b.endSec
      }
    }
    return cols
  }, [renderVideoBlocks, structured.videoOverlap])

  const audioColumns: ScriptBlock[][] = useMemo(() => {
    if (structured.audioOverlapCount < 2) {
      return [renderAudioBlocks]
    }
    // Greedy two-column packing: place each block in the first column where
    // its start time is >= the last block's end time.
    const cols: ScriptBlock[][] = [[], []]
    const colEnds = [0, 0]
    for (const b of renderAudioBlocks) {
      let placed = false
      for (let i = 0; i < cols.length; i++) {
        if (b.startSec >= colEnds[i]) {
          cols[i].push(b)
          colEnds[i] = b.endSec
          placed = true
          break
        }
      }
      if (!placed) {
        // Fall back to column 1; overlap is visible.
        cols[1].push(b)
        colEnds[1] = b.endSec
      }
    }
    return cols
  }, [renderAudioBlocks, structured.audioOverlapCount])

  // Compute the timeline span. Cover every block + 1s of padding so blocks
  // never visually clip the right edge of the ruler. Minimum span is 4s so
  // the ruler is always visible even when no blocks exist yet.
  const allTimedBlocks = [...renderVideoBlocks, ...renderAudioBlocks]
  const maxEndSec =
    allTimedBlocks.length === 0
      ? 4
      : Math.max(4, Math.ceil(allTimedBlocks.reduce((m, b) => Math.max(m, b.endSec), 0)) + 1)

  // Horizontal scale, driven by the timeline's zoom buttons.
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_TIMELINE_ZOOM_INDEX)
  const pixelsPerSecond = TIMELINE_ZOOM_LEVELS[zoomIndex]
  const handleZoom = useCallback(
    (direction: 'in' | 'out') => {
      setZoomIndex((prev) => {
        const next =
          direction === 'in'
            ? Math.min(TIMELINE_ZOOM_LEVELS.length - 1, prev + 1)
            : Math.max(0, prev - 1)
        avScriptLog({
          event: 'timeline-zoom',
          nodeId,
          direction,
          pixelsPerSecond: TIMELINE_ZOOM_LEVELS[next],
        })
        return next
      })
    },
    [nodeId],
  )

  /** Minimum height of one horizontal lane (or overlap sub-lane). Lanes
   * share the timeline panel's height and never grow to fit their content:
   * a block whose text does not fit scrolls inside itself, so the motion
   * prompt as a whole does not need a vertical scrollbar. */
  const MIN_LANE_HEIGHT_PX: Record<'video' | 'audio', number> = { video: 160, audio: 96 }

  /** Minimum visible width for a block (read view and in-place editor alike,
   * so opening the editor never resizes the block), so a 0.5s block is still
   * readable. The time scale stretches under short blocks to honour it. */
  const MIN_BLOCK_WIDTH_PX = 200

  // Clip-wide notes sit at the left of the same horizontal scroll, ahead of
  // the timeline's 0s mark.
  const hasNotesColumn = Boolean(
    structured.clipHeader || clipWideNotesText || structured.trailingText,
  )
  const notesColumnPx = hasNotesColumn ? NOTES_COLUMN_PX : 0

  // Stretch the time scale under any block that would be narrower than its
  // minimum width, instead of letting the block overflow into (and be covered
  // by) the next one. The ruler uses the same scale, so edges stay aligned.
  const timeScale = useMemo(
    () =>
      buildTimeScale(
        [...renderVideoBlocks, ...renderAudioBlocks].map((b) => ({
          startSec: b.startSec,
          endSec: b.endSec,
          minWidthPx: MIN_BLOCK_WIDTH_PX,
        })),
        maxEndSec,
        pixelsPerSecond,
      ),
    [renderVideoBlocks, renderAudioBlocks, maxEndSec, pixelsPerSecond],
  )

  const slotLeftFor = (block: ScriptBlock): number => TIMELINE_INSET_PX + timeScale.x(block.startSec)
  const slotWidthFor = (block: ScriptBlock): number =>
    // The floor only matters for zero-length blocks, which stretching cannot widen.
    Math.max(timeScale.x(block.endSec) - timeScale.x(block.startSec), MIN_BLOCK_WIDTH_PX)

  // The timeline scrolls sideways; widen it so the last block is never
  // clipped at the right edge.
  const timelineWidthPx = allTimedBlocks.reduce(
    (w, b) => Math.max(w, slotLeftFor(b) + slotWidthFor(b) + 8),
    TIMELINE_INSET_PX + timeScale.width,
  )

  // Opening an editor (including a freshly appended draft at the far right)
  // scrolls the timeline so the editor is fully in view.
  const timelineScrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const scroller = timelineScrollRef.current
    if (!scroller || editingId === null) return
    const block = [...renderVideoBlocks, ...renderAudioBlocks].find((b) => b.id === editingId)
    if (!block) return
    const left = notesColumnPx + TIMELINE_INSET_PX + timeScale.x(block.startSec)
    const right =
      left + Math.max(timeScale.x(block.endSec) - timeScale.x(block.startSec), MIN_BLOCK_WIDTH_PX)
    // The pinned add-block column overlays the scroller's right edge.
    const visibleWidth = scroller.clientWidth - TIMELINE_SIDE_COLUMN_PX
    if (left < scroller.scrollLeft) {
      scroller.scrollLeft = Math.max(0, left - 8)
    } else if (right > scroller.scrollLeft + visibleWidth) {
      scroller.scrollLeft = right - visibleWidth + 8
    }
  }, [editingId, renderVideoBlocks, renderAudioBlocks, timeScale, notesColumnPx])

  // A plain mouse wheel only emits vertical deltas; translate them to
  // sideways scrolling when the timeline has nothing to scroll vertically —
  // unless the wheel is over something that scrolls vertically itself (note
  // text, an open editor), which must keep the gesture to itself.
  const handleTimelineWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const scroller = e.currentTarget
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
    if (scroller.scrollHeight > scroller.clientHeight) return
    if (wheelStartsInVerticalScroller(e.target, scroller)) return
    scroller.scrollLeft += e.deltaY
  }, [])

  /** Render one absolutely-positioned block slot on the shared timeline,
   * spanning the lane's full (fixed) height from x(startSec) to x(endSec).
   * Round 4 item 2: when the slot is being edited, it swaps to the
   * ScriptBlockEditor in the SAME position and at the SAME size. */
  const renderTimelineSlot = (block: ScriptBlock, track: 'video' | 'audio') => {
    const isEditing = editingId === block.id
    return (
      <div
        key={block.id}
        className="absolute top-0 bottom-0 py-2 pr-2"
        style={{
          left: slotLeftFor(block),
          width: slotWidthFor(block),
          // Keep the editor (and its delete-confirm / mention popovers) above
          // neighbouring slots.
          zIndex: isEditing ? 10 : 1,
        }}
        data-block-id={block.id}
        data-block-kind={block.kind}
        data-block-editing={isEditing ? 'true' : 'false'}
      >
        {isEditing ? (
          <ScriptBlockEditor
            key={block.id}
            block={block}
            onSave={handleSaveBlock}
            onCancel={() => handleCancelBlockEdit(block.id)}
            onKindToggle={(blockId, from, to) => {
              avScriptLog({
                event: 'block-kind-toggle',
                nodeId,
                blockId,
                from,
                to,
              })
            }}
            canvasNodes={canvasNodes}
            bodyRef={activeBodyRef}
            isDraft={draftBlock?.block.id === block.id}
          />
        ) : (
          <ScriptBlockView
            block={block}
            hasOverlap={overlapById.has(block.id)}
            onEdit={() => handleEditBlock(block.id)}
            onDelete={() => handleDeleteBlock(block.id, track)}
            onTimestampCommit={(s, e) => handleTimestampCommit(block.id, track, s, e)}
            canvasNodes={canvasNodes}
            nodeId={nodeId}
          />
        )}
      </div>
    )
  }

  // Draggable divider between the Video and Audio tracks. The split is a
  // fraction of the tracks' height, computed from on-screen coordinates so it
  // works at any canvas zoom (the bottom panel is scaled) and in the expanded
  // popup alike.
  const [videoTrackFraction, setVideoTrackFraction] = useState(readStoredVideoTrackFraction)
  const tracksRef = useRef<HTMLDivElement>(null)
  const dividerDragRef = useRef<{ pointerId: number; prevBodyCursor: string } | null>(null)
  const latestFractionRef = useRef(videoTrackFraction)

  const endDividerDrag = useCallback(
    (el: Element, reason: string) => {
      const drag = dividerDragRef.current
      if (!drag) return
      dividerDragRef.current = null
      document.body.style.cursor = drag.prevBodyCursor
      if (el.hasPointerCapture(drag.pointerId)) el.releasePointerCapture(drag.pointerId)
      try {
        localStorage.setItem(VIDEO_TRACK_FRACTION_STORAGE_KEY, String(latestFractionRef.current))
      } catch {
        /* storage unavailable — the split just won't persist */
      }
      avScriptLog({
        event: 'track-divider-resize-end',
        nodeId,
        reason,
        videoTrackFraction: Math.round(latestFractionRef.current * 1000) / 1000,
      })
    },
    [nodeId],
  )

  // Unmounting mid-drag (e.g. the expanded popup closes) must not leave the
  // row-resize cursor stuck on the page.
  useEffect(
    () => () => {
      const drag = dividerDragRef.current
      if (drag) document.body.style.cursor = drag.prevBodyCursor
    },
    [],
  )

  const handleDividerPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || !e.isPrimary || dividerDragRef.current) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dividerDragRef.current = { pointerId: e.pointerId, prevBodyCursor: document.body.style.cursor }
    document.body.style.cursor = 'row-resize'
  }, [])

  const handleDividerPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dividerDragRef.current?.pointerId !== e.pointerId) return
      const tracks = tracksRef.current
      if (!tracks || tracks.offsetHeight <= 0) return
      const rect = tracks.getBoundingClientRect()
      // CSS px -> screen px (the bottom panel is zoom-scaled).
      const toScreen = rect.height / tracks.offsetHeight
      const minVideo = (MIN_LANE_HEIGHT_PX.video * videoColumns.length * toScreen) / rect.height
      const minAudio = (MIN_LANE_HEIGHT_PX.audio * audioColumns.length * toScreen) / rect.height
      const raw = (e.clientY - rect.top) / rect.height
      const next = Math.min(Math.max(raw, minVideo), Math.max(minVideo, 1 - minAudio))
      latestFractionRef.current = next
      setVideoTrackFraction(next)
    },
    [MIN_LANE_HEIGHT_PX.video, MIN_LANE_HEIGHT_PX.audio, videoColumns.length, audioColumns.length],
  )

  /** Render one track row: the lanes (one per packed sub-column — overlapping
   * blocks stack into additional sub-lanes) followed by the vertical
   * add-block button, pinned to the scroller's right edge. Round 4 item 3:
   * the audio track is a single "Dialogue & Sound Effect" track even when
   * split. */
  const renderTrack = (
    track: 'video' | 'audio',
    label: string,
    lanes: ScriptBlock[][],
  ) => (
    <div
      // Height split set by the draggable divider between the two tracks.
      className={`flex ${track === 'audio' ? 'border-b border-border-subtle' : ''}`}
      style={{
        flexGrow: track === 'video' ? videoTrackFraction : 1 - videoTrackFraction,
        flexBasis: 0,
      }}
      data-testid={`${track}-column`}
      role="group"
      aria-label={label}
    >
      <div
        className="flex flex-col flex-1"
        style={{ minWidth: timelineWidthPx }}
        data-testid={lanes.length > 1 ? `${track}-overlap-columns` : undefined}
      >
        {lanes.map((lane, i) => (
          <div
            key={i}
            className="relative flex-1"
            style={{ minHeight: MIN_LANE_HEIGHT_PX[track] }}
          >
            {lane.map((b) => renderTimelineSlot(b, track))}
          </div>
        ))}
      </div>
      <div
        className="sticky right-0 z-20 shrink-0 flex bg-surface-2 border-l border-border-subtle p-1.5"
        style={{ width: TIMELINE_SIDE_COLUMN_PX }}
      >
        <AddBlockButton
          track={track}
          onClick={() => handleAddBlock(track)}
          disabled={draftBlock !== null}
        />
      </div>
    </div>
  )

  const disabledTooltip =
    structured.disableReason?.reason === 'bgm-present'
      ? 'BGM lines are not supported in Structured view.'
      : 'Structured view requires timestamped segments.'

  return (
    <div className={fill ? 'relative flex flex-col flex-1 min-h-0' : 'relative'}>
      <div className="flex items-center justify-between mb-1 gap-2 shrink-0">
        <label className="text-xs font-medium uppercase tracking-wider text-text-muted">{t('videoGeneratorNode.motionPrompt')}</label>
        <div className="flex items-center gap-1.5">
          <ViewToggle
            mode={mode}
            onChange={handleModeChange}
            disabled={!structured.canStructured}
            disabledTooltip={disabledTooltip}
            nodeId={nodeId}
          />
          {onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="w-6 h-6 rounded flex items-center justify-center text-text-muted hover:text-foreground hover:bg-surface-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              title={expanded ? t('videoGeneratorNode.collapseMotionPrompt') : t('videoGeneratorNode.expandMotionPrompt')}
              aria-label={expanded ? t('videoGeneratorNode.collapseMotionPrompt') : t('videoGeneratorNode.expandMotionPrompt')}
              aria-pressed={expanded}
              data-testid="motion-prompt-expand-toggle"
            >
              {expanded ? (
                <Minimize2 className="w-3.5 h-3.5" aria-hidden />
              ) : (
                <Maximize2 className="w-3.5 h-3.5" aria-hidden />
              )}
            </button>
          )}
          {/*
            The overall edit pencil that used to live next to the toggle has
            moved. In Freeform mode it now lives INSIDE the freeform body so
            the user sees the affordance right where editing happens. In
            Structured mode there is no overall pencil at all — users only
            edit per-block via the per-block pencil that appears on hover.
          */}
        </div>
      </div>

      {mode === 'freeform' || !structured.canStructured ? (
        <div
          className={
            fill
              ? 'relative flex-1 min-h-0 overflow-y-auto nowheel bg-surface-2 border border-border-subtle rounded-lg p-2'
              : 'relative'
          }
          data-testid="freeform-body"
        >
          {/* In-body pencil (top-right). Always rendered in freeform mode so
              the user can enter the freeform editor regardless of whether
              the prompt parses into segments or falls back to a raw blob. */}
          <button
            onClick={onEdit}
            className="absolute top-0 right-0 w-5 h-5 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors z-10"
            title={t('videoGeneratorNode.edit')}
            data-testid="motion-prompt-edit-btn"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          {parsed.fallbackText ? (
            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap pr-7" data-testid="freeform-view">
              {renderTextWithChips(parsed.fallbackText!, candidatesMap, projectId)}
            </p>
          ) : parsed.segments.length === 0 ? (
            <p className="text-sm text-foreground/30 italic pr-7" data-testid="freeform-view">{t('videoGeneratorNode.noMotionPrompt')}</p>
          ) : (
            <div className="space-y-3 pr-7" data-testid="freeform-view">
              {parsed.clipHeader && (
                <p className="text-sm font-bold text-foreground/90 whitespace-pre-wrap">{parsed.clipHeader}</p>
              )}
              {parsed.segments.map((seg, i) => (
                <div key={i} className="space-y-1" data-testid="motion-segment">
                  {seg.type === 'voiceover' ? (
                    <div className="bg-surface-1 rounded-lg p-2 border border-border-subtle" data-testid="voiceover-card">
                      <span className="text-xxs uppercase tracking-wider text-primary/70 font-medium">{t('videoGeneratorNode.voiceover')}</span>
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium ml-2">
                        {seg.timestamp}
                      </span>
                      {seg.character && (
                        <p className="text-sm font-bold text-foreground/90 mt-1">
                          {seg.character}
                          {seg.emotion && (
                            <span className="text-xxs italic text-foreground/50 ml-1">{seg.emotion}</span>
                          )}
                        </p>
                      )}
                      <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap mt-0.5 italic">
                        &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                      </p>
                    </div>
                  ) : seg.type === 'dialogue' ? (
                    <div className="bg-surface-1 rounded-lg p-2 border border-border-subtle" data-testid="dialogue-card">
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                        {seg.timestamp}
                      </span>
                      {seg.character && (
                        <p className="text-sm font-bold text-foreground/90 mt-1">
                          {seg.character}
                          {seg.emotion && (
                            <span className="text-xxs italic text-foreground/50 ml-1">{seg.emotion}</span>
                          )}
                        </p>
                      )}
                      <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap mt-0.5">
                        &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                      </p>
                    </div>
                  ) : (
                    <>
                      <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                        {seg.timestamp}
                      </span>
                      <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                        {renderTextWithChips(seg.action, candidatesMap, projectId)}
                      </p>
                      {seg.camera && (
                        <p className="text-sm">
                          <span className="text-text-muted">{t('videoGeneratorNode.camera')}</span>{' '}
                          <span className="text-foreground/70">{seg.camera}</span>
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* Trailing content is no longer folded into the last segment's
                  body, so render it explicitly or it would look deleted. */}
              {parsed.trailingText && (
                <p
                  className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap"
                  data-testid="freeform-trailing"
                >
                  {renderTextWithChips(parsed.trailingText, candidatesMap, projectId)}
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`flex flex-col bg-surface-2 border border-border-subtle rounded-lg overflow-hidden ${fill ? 'flex-1 min-h-0' : 'h-80'}`}
          data-testid="structured-view"
        >
          {/* One horizontal scroll for the whole motion prompt: the clip-wide
              notes column at the far left, then the shared timeline — the
              Video track and the Dialogue & SFX track (each may split into
              stacked sub-lanes on overlap) with the ruler pinned along the
              bottom. Blocks are offset by timeScale.x(startSec) so every
              block lines up with the ruler ticks. The add-block buttons and
              zoom controls stay pinned to the right edge. */}
          <div
            ref={timelineScrollRef}
            onWheel={handleTimelineWheel}
            className="flex-1 min-h-0 overflow-auto nowheel"
            data-testid="av-timeline"
          >
            {/* Fixed (not min) height so the notes column has a definite
                height for its 50% trailing-notes card; taller lanes overflow
                the row and the scroller still scrolls vertically. */}
            <div className="flex h-full w-max min-w-full">
              {hasNotesColumn && (
                <div
                  className="shrink-0 flex flex-col gap-2 min-h-0 border-r border-border-subtle p-2"
                  style={{ width: NOTES_COLUMN_PX }}
                  data-testid="structured-notes"
                >
                  {structured.clipHeader && (
                    <p className="shrink-0 text-sm font-bold text-foreground/90">{structured.clipHeader}</p>
                  )}
                  {clipWideNotesText && (
                    <ClipWideNote
                      label={t('videoGeneratorNode.clipWideNotes')}
                      text={clipWideNotesText}
                      testId="structured-preamble"
                      // Fills whatever the trailing notes leave free.
                      sizeClassName="flex-1"
                      onCommit={(newText) => commitNotes('preamble', newText)}
                      editLabel={t('videoGeneratorNode.edit')}
                      candidatesMap={candidatesMap}
                      canvasNodes={canvasNodes}
                      projectId={projectId}
                    />
                  )}
                  {structured.trailingText && (
                    <ClipWideNote
                      label={t('videoGeneratorNode.clipWideTrailing')}
                      text={structured.trailingText}
                      testId="structured-trailing"
                      // Fixed at half the column (minus half the gap),
                      // whether or not clip-wide notes are present.
                      sizeClassName="shrink-0 h-[calc(50%-4px)]"
                      onCommit={(newText) => commitNotes('trailing', newText)}
                      editLabel={t('videoGeneratorNode.edit')}
                      candidatesMap={candidatesMap}
                      canvasNodes={canvasNodes}
                      projectId={projectId}
                    />
                  )}
                </div>
              )}
              <div className="flex-1 flex flex-col" data-testid="motion-timeline">
                <div ref={tracksRef} className="flex-1 min-h-0 flex flex-col">
                  {renderTrack('video', t('videoGeneratorNode.videoLabel'), videoColumns)}
                  {/* Drag to change the Video / Audio height split. */}
                  <div
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label={t('videoGeneratorNode.resizeTracks')}
                    title={t('videoGeneratorNode.resizeTracks')}
                    onPointerDown={handleDividerPointerDown}
                    onPointerMove={handleDividerPointerMove}
                    onPointerUp={(e) => endDividerDrag(e.currentTarget, 'pointerup')}
                    onPointerCancel={(e) => endDividerDrag(e.currentTarget, 'pointercancel')}
                    onLostPointerCapture={(e) => endDividerDrag(e.currentTarget, 'lostpointercapture')}
                    className="group relative h-1.5 shrink-0 cursor-row-resize touch-none"
                    data-testid="av-track-divider"
                  >
                    <span
                      className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border-subtle transition-colors group-hover:h-1 group-hover:bg-chrome/20"
                      aria-hidden
                    />
                  </div>
                  {renderTrack('audio', t('videoGeneratorNode.dialogueSoundEffect'), audioColumns)}
                </div>
                <div className="sticky bottom-0 z-20 flex bg-surface-2">
                  <div className="flex-1" style={{ minWidth: timelineWidthPx }}>
                    <TimeRuler maxSec={maxEndSec} scale={timeScale} />
                  </div>
                  <div
                    className="sticky right-0 shrink-0 flex items-center justify-center gap-1 bg-surface-2 border-l border-border-subtle"
                    style={{ width: TIMELINE_SIDE_COLUMN_PX }}
                  >
                    <button
                      type="button"
                      onClick={() => handleZoom('out')}
                      disabled={zoomIndex === 0}
                      title={t('timeRuler.zoomOut')}
                      aria-label={t('timeRuler.zoomOut')}
                      className="w-5 h-5 rounded flex items-center justify-center text-text-muted hover:text-foreground hover:bg-surface-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      data-testid="av-timeline-zoom-out"
                    >
                      <ZoomOut className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleZoom('in')}
                      disabled={zoomIndex === TIMELINE_ZOOM_LEVELS.length - 1}
                      title={t('timeRuler.zoomIn')}
                      aria-label={t('timeRuler.zoomIn')}
                      className="w-5 h-5 rounded flex items-center justify-center text-text-muted hover:text-foreground hover:bg-surface-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      data-testid="av-timeline-zoom-in"
                    >
                      <ZoomIn className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}),
)

FormattedMotionPrompt.displayName = 'FormattedMotionPrompt'

/* ── Add Dialogue Form ── */

const ADD_DIALOGUE_LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-0.5'
const ADD_DIALOGUE_FIELD_CLASS = 'rounded-lg bg-input-bg border border-input-border px-2 py-1 text-xs font-medium text-foreground placeholder:text-text-muted transition-colors hover:bg-surface-0 hover:border-input-border-hover focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

const parseFiniteTimeInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const clampTimeToDuration = (value: number, duration: number): number => {
  return Math.min(Math.max(value, 0), duration)
}

const formatPromptTime = (value: number): string => {
  return (Math.round(value * 10) / 10).toFixed(1)
}

export const AddDialogueForm = memo(function AddDialogueForm({
  duration,
  characterNames,
  voiceRoleOptions,
  clipIndex,
  initialEntry,
  submitLabel,
  onAdd,
  onCancel,
}: {
  duration: number
  characterNames: string[]
  voiceRoleOptions: string[]
  clipIndex: number
  initialEntry?: DialogueEntry
  submitLabel?: string
  onAdd: (entry: DialogueEntry) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('canvas')
  const maxDuration = Number.isFinite(duration) && duration > 0 ? duration : 8
  const initialVoice = splitVoiceText(initialEntry?.voice_text ?? '')
  const characterListId = useId()
  const voiceRoleListId = useId()
  const [type, setType] = useState<'dialogue' | 'voiceover'>(
    initialEntry?.type === 'voiceover' ? 'voiceover' : 'dialogue',
  )
  const [character, setCharacter] = useState(initialEntry?.voice_character ?? characterNames[0] ?? '')
  const [voiceRoleId, setVoiceRoleId] = useState(initialEntry?.voice_role_id ?? '')
  const [startS, setStartS] = useState(formatPromptTime(initialEntry?.start_s ?? 1.0))
  const [endS, setEndS] = useState(formatPromptTime(initialEntry?.end_s ?? Math.min(3.0, maxDuration)))
  const [emotion, setEmotion] = useState(initialVoice.emotion ?? '')
  const [text, setText] = useState(initialVoice.text)
  const characterOptions = useMemo(() => {
    const names = [...characterNames]
    const trimmedCharacter = character.trim()
    if (trimmedCharacter && !names.includes(trimmedCharacter)) {
      names.unshift(trimmedCharacter)
    }
    return names
  }, [character, characterNames])
  const roleOptions = useMemo(() => {
    const names = [...voiceRoleOptions]
    const trimmedRole = voiceRoleId.trim()
    if (trimmedRole && !names.includes(trimmedRole)) {
      names.unshift(trimmedRole)
    }
    return names
  }, [voiceRoleId, voiceRoleOptions])

  useEffect(() => {
    if (!character.trim() && characterNames.length > 0) {
      setCharacter(characterNames[0])
    }
  }, [character, characterNames])

  const timingState = useMemo(() => {
    const parsedStart = parseFiniteTimeInput(startS)
    const parsedEnd = parseFiniteTimeInput(endS)

    if (parsedStart === null || parsedEnd === null) {
      return {
        isValid: false,
        message: t('audioEditorModal.enterFiniteTimes'),
        start: 0,
        end: 0,
      }
    }

    const clampedStart = clampTimeToDuration(parsedStart, maxDuration)
    const clampedEnd = clampTimeToDuration(parsedEnd, maxDuration)

    if (clampedEnd <= clampedStart) {
      return {
        isValid: false,
        message: t('audioEditorModal.endAfterStart'),
        start: clampedStart,
        end: clampedEnd,
      }
    }

    const willClamp = clampedStart !== parsedStart || clampedEnd !== parsedEnd
    return {
      isValid: true,
      message: willClamp
        ? `Will save as ${formatPromptTime(clampedStart)}s-${formatPromptTime(clampedEnd)}s.`
        : '',
      start: clampedStart,
      end: clampedEnd,
    }
  }, [endS, maxDuration, startS, t])

  const canAdd = Boolean(text.trim() && character.trim() && timingState.isValid)

  const handleAdd = () => {
    const trimmedText = text.trim()
    if (!trimmedText || !character.trim() || !timingState.isValid) return

    const entry: DialogueEntry = {
      clip_index: clipIndex,
      voice_character: character.trim(),
      voice_text: emotion.trim() ? `[${emotion.trim()}] ${trimmedText}` : trimmedText,
      start_s: timingState.start,
      end_s: timingState.end,
      type,
      voice_role_id: '',
    }
    const resolvedVoiceRoleId = voiceRoleId.trim() || (type === 'voiceover' ? character.trim() : '')
    if (resolvedVoiceRoleId) {
      entry.voice_role_id = resolvedVoiceRoleId
    }
    onAdd(entry)
  }

  return (
    <div className="mt-2 rounded-lg border border-input-border bg-surface-1 p-3 space-y-2" data-testid="add-dialogue-form">
      {/* Type toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setType('dialogue')}
          className={`h-7 rounded-full border px-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            type === 'dialogue'
              ? 'border-primary/50 bg-primary/20 text-primary'
              : 'border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground'
          }`}
          data-testid="dialogue-type-btn"
        >
          {t('videoGeneratorNode.dialogueType')}
        </button>
        <button
          onClick={() => setType('voiceover')}
          className={`h-7 rounded-full border px-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
            type === 'voiceover'
              ? 'border-primary/50 bg-primary/20 text-primary'
              : 'border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground'
          }`}
          data-testid="voiceover-type-btn"
        >
          {t('videoGeneratorNode.narratorType')}
        </button>
      </div>

      {/* Speaker + narrator voice role */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={ADD_DIALOGUE_LABEL_CLASS}>{t('audioEditorModal.speakerLabel')}</label>
          <input
            value={character}
            onChange={(e) => setCharacter(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder={t('audioEditorModal.characterOrNarrator')}
            list={characterOptions.length > 0 ? characterListId : undefined}
            data-testid="dialogue-character-input"
          />
          {characterOptions.length > 0 && (
            <datalist id={characterListId}>
              {characterOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          )}
        </div>
        <div>
          <label className={ADD_DIALOGUE_LABEL_CLASS}>{t('audioEditorModal.voiceRoleLabel')} <span className="normal-case">{t('audioEditorModal.optional')}</span></label>
          <input
            value={voiceRoleId}
            onChange={(e) => setVoiceRoleId(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder={type === 'voiceover' ? 'opening_narrator' : 'role id'}
            list={roleOptions.length > 0 ? voiceRoleListId : undefined}
            data-testid="dialogue-voice-role-input"
          />
          {roleOptions.length > 0 && (
            <datalist id={voiceRoleListId}>
              {roleOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          )}
        </div>
      </div>

      {/* Timing + Emotion row */}
      <div className="flex gap-2">
        <div className="flex-1">
          <label className={ADD_DIALOGUE_LABEL_CLASS}>{t('audioEditorModal.timing')}</label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={startS}
              onChange={(e) => setStartS(e.target.value)}
              className={`h-7 w-16 text-center ${ADD_DIALOGUE_FIELD_CLASS}`}
              min={0}
              max={maxDuration}
              step={0.5}
              aria-invalid={!timingState.isValid}
              data-testid="dialogue-start-time"
            />
            <span className="text-text-muted text-xs font-medium">–</span>
            <input
              type="number"
              value={endS}
              onChange={(e) => setEndS(e.target.value)}
              className={`h-7 w-16 text-center ${ADD_DIALOGUE_FIELD_CLASS}`}
              min={0}
              max={maxDuration}
              step={0.5}
              aria-invalid={!timingState.isValid}
              data-testid="dialogue-end-time"
            />
            <span className="text-text-muted text-xxs">sec</span>
          </div>
          {!timingState.isValid && (
            <p className="mt-1 text-xxs text-destructive" data-testid="dialogue-timing-error">
              {timingState.message}
            </p>
          )}
          {timingState.isValid && timingState.message && (
            <p className="mt-1 text-xxs text-text-muted" data-testid="dialogue-timing-hint">
              {timingState.message}
            </p>
          )}
        </div>
        <div className="flex-1">
          <label className={ADD_DIALOGUE_LABEL_CLASS}>{t('audioEditorModal.emotionLabel')} <span className="normal-case">{t('audioEditorModal.optional')}</span></label>
          <input
            value={emotion}
            onChange={(e) => setEmotion(e.target.value)}
            className={`h-7 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
            placeholder={t('audioEditorModal.emotionPlaceholder')}
            data-testid="dialogue-emotion-input"
          />
        </div>
      </div>

      {/* Dialogue text */}
      <div>
        <label className={ADD_DIALOGUE_LABEL_CLASS}>{t('audioEditorModal.lineLabel')}</label>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={`h-8 w-full ${ADD_DIALOGUE_FIELD_CLASS}`}
          placeholder={t('audioEditorModal.linePlaceholder')}
          data-testid="dialogue-text-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
            if (e.key === 'Escape') onCancel()
          }}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="h-7 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="dialogue-cancel-btn"
        >
          {t('videoGeneratorNode.cancel')}
        </button>
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          className="h-7 rounded-lg border border-primary/40 bg-primary/20 px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/30 hover:border-primary/60 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted disabled:hover:bg-surface-1 disabled:hover:border-input-border focus:outline-none focus:ring-2 focus:ring-primary/20"
          data-testid="dialogue-add-btn"
        >
          {submitLabel ?? t('videoGeneratorNode.addDialogueType', { type: type === 'voiceover' ? t('videoGeneratorNode.narratorType') : t('videoGeneratorNode.dialogueType') })}
        </button>
      </div>
    </div>
  )
})

export const DialoguePromptPanel = memo(function DialoguePromptPanel({
  entries,
  promptEntries,
  hasStoredDialogue,
  hasIncomingEdge,
  showDialogueForm,
  duration,
  characterNames,
  voiceRoleOptions,
  clipIndex,
  onShowForm,
  onAdd,
  onUpdate,
  onDelete,
  onCancelAdd,
}: {
  entries: DialogueEntry[]
  promptEntries: DialogueEntry[]
  hasStoredDialogue: boolean
  hasIncomingEdge: boolean
  showDialogueForm: boolean
  duration: number
  characterNames: string[]
  voiceRoleOptions: string[]
  clipIndex: number
  onShowForm: () => void
  onAdd: (entry: DialogueEntry) => void
  onUpdate: (index: number, entry: DialogueEntry) => void
  onDelete: (index: number) => void
  onCancelAdd: () => void
}) {
  const { t } = useTranslation('canvas')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= entries.length) {
      setEditingIndex(null)
    }
  }, [editingIndex, entries.length])

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-0 p-3" data-testid="structured-dialogue-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted">{t('audioEditorModal.dialogueScript')}</label>
            <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
              {t('videoGeneratorNode.audioPrompt')}
            </span>
            {hasIncomingEdge && (
              <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xxs text-primary">
                {t('videoGeneratorNode.connected')}
              </span>
            )}
          </div>
          <p className="mt-1 text-xxs text-text-muted">
            {t('videoGeneratorNode.editsAffectSession')}
          </p>
        </div>
      </div>

      {hasIncomingEdge && (
        <p className="mt-2 rounded-lg border border-primary/20 bg-primary/10 px-2 py-1 text-xxs text-primary/90" data-testid="dialogue-connected-warning">
          {t('videoGeneratorNode.connectedDialogueWarning')}
        </p>
      )}

      <div className="mt-3 grid gap-3 lg:grid-cols-2" data-testid="audio-editor-dialogue-comparison">
        <div className="flex min-h-0 flex-col rounded-lg border border-input-border bg-input-bg p-2" data-testid="dialogue-motion-cues">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground">{t('audioEditorModal.motionPromptCues')}</p>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                {formatLineCount(promptEntries.length, t)}
              </span>
              <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                {t('videoGeneratorNode.readOnly')}
              </span>
            </div>
          </div>
          <p className="mt-1 text-xxs leading-relaxed text-text-muted">
            {t('videoGeneratorNode.sourceCuesDescription')}
          </p>
          {promptEntries.length > 0 ? (
            <div className="mt-2 space-y-1.5">
              {promptEntries.map((entry, index) => {
                const preview = getDialogueEntryPreview(entry, t)
                return (
                  <div
                    key={`motion-${preview.speaker}-${preview.time}-${index}`}
                    className="rounded-md border border-border-subtle bg-surface-0 px-2 py-1.5"
                    data-testid="dialogue-motion-cue-row"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full border border-input-border bg-surface-1 px-1.5 py-0.5 text-xxs text-text-muted">
                        {preview.time}
                      </span>
                      <span className="text-xxs uppercase tracking-wider text-text-muted">{preview.type}</span>
                      <span className="text-xs font-medium text-foreground">{preview.speaker}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-xs font-medium leading-relaxed text-text-muted">{preview.text}</p>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="mt-2 rounded-md border border-border-subtle bg-surface-0 px-2 py-2 text-xs font-medium text-text-muted" data-testid="dialogue-motion-cues-empty">
              {t('videoGeneratorNode.noMotionCuesFound')}
            </p>
          )}
        </div>

        <div className="flex min-h-0 flex-col rounded-lg border border-primary/25 bg-primary/10 p-2" data-testid="dialogue-audio-script">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-foreground">{t('audioEditorModal.editableAudioScript')}</p>
            <span className="rounded-full border border-primary/30 bg-background/30 px-1.5 py-0.5 text-xxs text-primary">
              {formatLineCount(entries.length, t)}
            </span>
          </div>
          <p className="mt-1 text-xxs leading-relaxed text-text-muted">
            {t('videoGeneratorNode.useThisScript')}
          </p>

          <div className="mt-2 space-y-2">
            {entries.length > 0 ? (
              entries.map((entry, i) => {
                const { text, emotion } = splitVoiceText(entry.voice_text ?? '')
                const type = entry.type === 'voiceover' ? 'voiceover' : 'dialogue'
                if (editingIndex === i) {
                  return (
                    <AddDialogueForm
                      key={`edit-${i}`}
                      duration={duration}
                      characterNames={characterNames}
                      voiceRoleOptions={voiceRoleOptions}
                      clipIndex={clipIndex}
                      initialEntry={entry}
                      submitLabel={t('videoGeneratorNode.saveDialogue')}
                      onAdd={(updatedEntry) => {
                        onUpdate(i, updatedEntry)
                        setEditingIndex(null)
                      }}
                      onCancel={() => setEditingIndex(null)}
                    />
                  )
                }

                return (
                  <div
                    key={`${entry.voice_character}-${entry.start_s}-${entry.end_s}-${i}`}
                    className="rounded-lg border border-primary/20 bg-background/25 p-2"
                    data-testid="dialogue-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        {/* eslint-disable i18next/no-literal-string -- time-unit notation */}
                        <span className="inline-block rounded-full bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary/80">
                          {formatPromptTime(entry.start_s ?? 0)}s-{formatPromptTime(entry.end_s ?? 0)}s
                        </span>
                        {/* eslint-enable i18next/no-literal-string */}
                        {type === 'voiceover' && (
                          <span className="text-xxs uppercase tracking-wider text-primary/70">{t('videoGeneratorNode.narrator')}</span>
                        )}
                        {entry.voice_role_id && (
                          <span className="rounded-full border border-input-border bg-surface-0 px-1.5 py-0.5 text-xxs text-text-muted">
                            {entry.voice_role_id}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            onCancelAdd()
                            setEditingIndex(i)
                          }}
                          className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                          data-testid="dialogue-edit-btn"
                        >
                          {t('videoGeneratorNode.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            onDelete(i)
                            setEditingIndex(null)
                          }}
                          className="h-6 rounded-md border border-input-border bg-surface-0 px-1.5 text-xxs text-text-muted transition-colors hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-destructive/20"
                          data-testid="dialogue-delete-btn"
                        >
                          {t('videoGeneratorNode.delete')}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1 text-sm font-bold text-foreground/90">
                      {entry.voice_character}
                      {emotion && (
                        <span className="ml-1 text-xxs italic text-foreground/50">{emotion}</span>
                      )}
                    </p>
                    <p className={`mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80 ${type === 'voiceover' ? 'italic' : ''}`}>
                      &ldquo;{text}&rdquo;
                    </p>
                  </div>
                )
              })
            ) : (
              <p className="rounded-lg border border-primary/20 bg-background/25 px-2 py-2 text-sm text-text-muted" data-testid="dialogue-empty-state">
                {t('videoGeneratorNode.noEditableAudioScript')}
              </p>
            )}
          </div>

          {showDialogueForm ? (
            <AddDialogueForm
              duration={duration}
              characterNames={characterNames}
              voiceRoleOptions={voiceRoleOptions}
              clipIndex={clipIndex}
              onAdd={onAdd}
              onCancel={onCancelAdd}
            />
          ) : (
            <button
              onClick={() => {
                setEditingIndex(null)
                onShowForm()
              }}
              className="group mt-2 flex h-7 items-center gap-1.5 rounded-lg border border-input-border bg-surface-1 px-2 text-xs font-medium text-text-muted transition-colors hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="add-dialogue-btn"
            >
              <MessageSquarePlus className="h-3.5 w-3.5 text-primary/70 transition-colors group-hover:text-primary" />
              {t('videoGeneratorNode.addDialogueOrNarrator')}
            </button>
          )}

          {!hasStoredDialogue && promptEntries.length > 0 && (
            <p className="mt-2 text-xxs text-text-muted" data-testid="dialogue-import-required-note">
              {t('videoGeneratorNode.importCopiesMotionCues')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
})

/* ── Main Component ── */

export const VideoGeneratorNode = memo(function VideoGeneratorNode(
  props: NodeProps<VideoGeneratorNodeData>,
) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { previewStyle, mediaFit } = useNodePresentationPreview()
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const label = dataStrOpt(data, 'label')

  const node = useCanvasNode(id, 'videoGenerator', data)
  const in_name = node?.get('in_name')
  const out_video = node?.get('out_video')
  const prompt = node?.get('in_motion_prompt') ?? ''
  const in_clip_number = node?.get('in_clip_number')
  const clipNumberName =
    typeof in_clip_number === 'number' && Number.isFinite(in_clip_number) && in_clip_number > 0
      ? `Clip ${in_clip_number}`
      : ''
  // "Scene 1" / "Grid Video 1" are Director-assigned defaults stored in English
  // (the backend re-parses them for ordinals and @-mention lookup), so they are
  // localized on the way out only. Returns undefined once the user renames, and
  // the existing in_name → clipNumber → label precedence is otherwise untouched.
  const localizedDefaultName = localizeDefaultNodeName(
    'videoGenerator',
    { in_name, name_autogen: node?.get('name_autogen') },
    t,
  )
  const displayName = localizedDefaultName || in_name || clipNumberName || label || ''
  const duration = node?.get('in_duration') ?? 5
  const storedStyleId = node?.get('in_style')
  const model = node?.get('in_model') ?? DEFAULT_VIDEO_MODEL_ID
  const referenceMode = node?.get('in_mode') !== 'FIRST_LAST'
  const isLocalMotionImage = node?.get('in_mode') === 'MOTION_IMAGE'
  const resolution = effectiveVideoResolution(model, node?.get('in_resolution') ?? 'auto')
  const aspect_ratio = node?.get('in_aspect_ratio') ?? '16:9'
  const { providerGenerateAudio } =
    resolveVideoGeneratorAudioDisplaySettings({
      has: (key) => {
        if (key === 'in_provider_generate_audio') {
          return node?.has('in_provider_generate_audio') ?? false
        }
        return node?.has('in_generate_audio') ?? false
      },
      get: (key) => {
        if (key === 'in_provider_generate_audio') {
          return node?.get('in_provider_generate_audio')
        }
        return node?.get('in_generate_audio')
      },
    })
  const allow_bgm = node?.get('in_allow_bgm') ?? false
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const referenceVideoEdits = videoReferenceEditsToJson(node?.get('in_reference_video_edits') ?? data.in_reference_video_edits)
  const generated_video = out_video
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const {
    native: nativeModels,
    byom: byomModels,
    isReady: videoModelsReady,
  } = useVideoModels()

  const [assetDisplayDimensions, setAssetDisplayDimensions] = useState<SnappedDisplayDimensions | null>(null)
  const [loadedVideoDimensions, setLoadedVideoDimensions] = useState<{
    path: string
    dimensions: VideoDimensions
  } | null>(null)
  const requestedAspectRatio = ASPECT_RATIO_MAP[aspect_ratio] ?? '16/9'
  const cssAspectRatio = assetDisplayDimensions?.aspectRatio ?? requestedAspectRatio
  const nodeWidth = assetDisplayDimensions?.width ?? ASPECT_WIDTH_MAP[aspect_ratio] ?? 315

  const hasGeneratedVideo = Boolean(out_video)
  const depthVideoAction = useDepthVideoAction({ nodeId: id, hasVideo: hasGeneratedVideo })

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const selectSingleCanvasNode = useSelectSingleCanvasNode()
  const canvasNodes = useNodes<ReactFlowCanvasNodeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const styleEdge = edges.find((edge) => edge.target === id && edge.targetHandle === 'in_style')
  const styleSource = styleEdge && canvasNodes.find((source) => source.id === styleEdge.source)
  const styleSourceNode = styleSource?.type === 'styleSelector' && canvas?.getYNode?.(styleSource.id)
  const selectedStyleId = styleSourceNode
    ? bindCanvasNodeByFeType(styleSourceNode, 'styleSelector').get('out_style')
    : storedStyleId
  const connectedStyleSourceId = styleEdge?.source
  useEffect(() => {
    videoStyleLog({
      event: 'video-style:effective-style',
      node_id: id,
      style_id: selectedStyleId,
      source: connectedStyleSourceId ? 'connection' : 'node',
      source_node_id: connectedStyleSourceId ?? null,
    })
  }, [id, selectedStyleId, connectedStyleSourceId])
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const uploadFile = useUploadFile()
  const { handleDelete, handleDuplicate, handleDownload: downloadFile, handleAddToChat } = useNodeActions({ nodeId: id })
  const downloadPreparing = useIsPreparingC2paDownload(id)
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, displayName || t('videoGeneratorNode.untitledVideoGenerator'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })

  // Video preview hover state
  const [isHovering, setIsHovering] = useState(false)
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)
  // Restoring the full card mounts a new preview under a stationary pointer.
  // It may not receive mouseenter until the pointer moves, so the host's
  // pointer hover must also activate the newly mounted player.
  const isPreviewHovering = isHovering || (lightweightNodeChrome && isHoveringLightweightNodeChrome)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const videoPreviewRef = useRef<VideoPreviewPlayerHandle>(null)
  const selectThisVideoNode = useCallback(() => {
    selectSingleCanvasNode(id)
  }, [id, selectSingleCanvasNode])

  // Suspend canvas keyboard shortcuts (Delete/Backspace node deletion, etc.)
  // while this node's video lightbox is open. See canvasShortcutGuard.
  useBlockingOverlay(isVideoModalOpen)

  // Escape key dismisses the video lightbox.
  useEffect(() => {
    if (!isVideoModalOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsVideoModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isVideoModalOpen])

  // Bottom panel state
  const [bottomPanelTab, setBottomPanelTab] = useState('config')
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const variantsFocusRequestToken = 0
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  // True when any per-block editor inside Structured view is open. We lift
  // this state up from FormattedMotionPrompt so the Config tab can keep the
  // CINEMATOGRAPHY PRESETS panel visible while a structured block editor is
  // active — same UX affordance that already exists for freeform editing.
  const [isStructuredEditing, setIsStructuredEditing] = useState(false)
  // The Config tab's motion prompt section can move into a large popup that
  // is easier to edit than the canvas-scaled bottom panel.
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const togglePromptExpanded = useCallback(() => setIsPromptExpanded((prev) => !prev), [])
  const closePromptExpanded = useCallback(() => setIsPromptExpanded(false), [])
  const richTextBoxRef = useRef<RichTextBoxHandle>(null)
  // Round 4 item 4: imperative handle into FormattedMotionPrompt so the
  // parent's preset panel can target either the open block editor's body
  // (insert at caret) or append a new video block (when no editor is open).
  const formattedMotionPromptRef = useRef<FormattedMotionPromptHandle>(null)
  // Round 4 item 6: lightweight undo stack of previous prompt strings so
  // Cmd+Z reverts the last preset insertion (or any other preset-driven
  // write). The stack is scoped to the lifetime of this node; YDoc's own
  // CRDT history is not exposed here.
  const prevPromptStackRef = useRef<string[]>([])
  const [localPrompt, setLocalPrompt] = useState(prompt)
  const isEditingPromptRef = useRef(isEditingPrompt)
  isEditingPromptRef.current = isEditingPrompt
  const localPromptRef = useRef(localPrompt)
  localPromptRef.current = localPrompt
  // Sync local prompt when external data changes
  useEffect(() => {
    if (!isEditingPrompt) setLocalPrompt(prompt)
  }, [prompt, isEditingPrompt])

  useEffect(() => {
    if (isEditingPrompt) {
      logCreativeTextLimitConfigured('video-motion-prompt', { nodeId: id })
    }
  }, [id, isEditingPrompt])

  // Declared before the parse memos: a comma-bearing voice-profile name is only
  // separable from an emotion suffix if the parser knows the connected names
  // (RFC-1203 / RFC-1204).
  const voiceProfileEdgesForSpeakers = useMemo(
    () => edges.filter((edge) => edge.target === id && edge.targetHandle === 'in_voice_profiles'),
    [edges, id],
  )
  const knownSpeakerIndex = useKnownSpeakerIndex(voiceProfileEdgesForSpeakers)
  const parsedPrompt = useMemo(
    () => parseMotionPrompt(prompt, knownSpeakerIndex),
    [prompt, knownSpeakerIndex],
  )
  const motionScript = useMemo(
    () => parseScript(prompt, knownSpeakerIndex),
    [prompt, knownSpeakerIndex],
  )
  // Proof, in NAD logs, that a comma-bearing profile survived parsing. Counts
  // only — never the names themselves, which are user content.
  useEffect(() => {
    const commaBearingSpeakers = parsedPrompt.segments.filter(
      (segment) => segment.character?.includes(','),
    ).length
    if (commaBearingSpeakers === 0 && knownSpeakerIndex.rawNames.length === 0) return
    avScriptLog({
      event: 'speaker-resolution',
      nodeId: id,
      knownProfileCount: knownSpeakerIndex.rawNames.length,
      commaBearingSpeakers,
      speakerSegments: parsedPrompt.segments.filter(
        (segment) => segment.type === 'dialogue' || segment.type === 'voiceover',
      ).length,
    })
  }, [id, knownSpeakerIndex, parsedPrompt])

  const motionCanGenerate = motionScript.videoOverlap.length === 0
  const motionGenerateBlockReasonCode: VideoBlockReason | undefined = motionCanGenerate
    ? undefined
    : { code: 'segmentsOverlap' }
  const in_voice_profiles = node?.get('in_voice_profiles') ?? data.in_voice_profiles
  const voiceProfiles = useMemo(
    (): ConcreteVoiceProfile[] => {
      const profiles: VoiceProfile[] = in_voice_profiles ?? []
      return profiles.filter(isConcreteVoiceProfile)
    },
    [in_voice_profiles],
  )
  const voiceProfileEdges = voiceProfileEdgesForSpeakers
  const hasVoiceProfileEdge = voiceProfileEdges.length > 0
  useEffect(() => {
    voiceProfileSourceLog({
      event: 'voice-profile-source:state',
      node_id: id,
      connected: hasVoiceProfileEdge,
      edge_count: voiceProfileEdges.length,
      profile_count: voiceProfiles.length,
    })
  }, [hasVoiceProfileEdge, id, voiceProfileEdges.length, voiceProfiles.length])
  // History
  const historyEntries: VideoHistoryEntry[] = useMemo(
    () => {
      void cacheKey
      return generatedVideoHistoryToJson(generated_video?.history)
    },
    [generated_video, cacheKey],
  )
  const pickedIndex = getPickedVideoIndex(generated_video)
  const videoPath = useMemo(() => {
    const selectedHistoryPath = videoHistoryPath(historyEntries[pickedIndex])
    if (selectedHistoryPath) return selectedHistoryPath
    return undefined
  }, [historyEntries, pickedIndex])
  const sourceDimensions =
    loadedVideoDimensions && loadedVideoDimensions.path === videoPath
      ? loadedVideoDimensions.dimensions
      : videoDimensionsFromMetadata(historyEntries[pickedIndex]?.result?.metadata)
  const videoTabs = useVideoTabs()
  // The expanded motion prompt popup lives inside the Config tab's panel: if
  // the node is deselected or another tab opens, the popup unmounts, so clear
  // the flag too — otherwise reopening Config would pop it straight back up.
  useEffect(() => {
    if (!selected || bottomPanelTab !== VIDEO_GENERATOR_CONFIG_TAB_ID) setIsPromptExpanded(false)
  }, [selected, bottomPanelTab])

  const [extractAudioRunningMode, setExtractAudioRunningMode] = useState<ExtractVideoAudioMode | null>(null)
  const isDialogueExtractionRunning = extractAudioRunningMode === 'dialogues'
  const isRunning = status === 'running'
  const activeAction = dataStrOpt(data, 'action')
  const generationGrace = useGenerationGracePeriod({
    projectId,
    canvasId,
    nodeId: id,
    nodeType: 'video_generator',
    isBackendRunning: isRunning,
  })
  const effectiveActiveAction = isDialogueExtractionRunning ? 'extract_dialogues' : activeAction
  const runningPresentation = getVideoRunningPresentation(effectiveActiveAction)
  const isGenerating = isRunning || generationGrace.isLocallyGenerating || isDialogueExtractionRunning
  // Older in-flight nodes do not have an action. Preserve their historical
  // render treatment while making known text-only work explicit.
  const isVideoRenderWork =
    isGenerating && runningPresentation.messageKey === 'videoGeneratorNode.renderingVideo'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isGenerating || isPendingExecution || isPendingCost
  const { action: upscaleVideoAction, flyout: upscaleVideoFlyout } = useUpscaleVideoAction({
    nodeId: id,
    hasVideo: Boolean(videoPath),
    isBusy: showSpinner,
    sourceName: displayName,
    sourceDimensions,
    sourceVideoPath: videoPath,
  })
  const shouldRenderNodeChrome =
    !lightweightNodeChrome || !!selected || isHoveringLightweightNodeChrome
  const batchPending = isRunning ? ((node?.get('batch_pending') as number | undefined) ?? 0) : 0
  const previewIntrinsicWidth = Math.max(1, nodeWidth - VIDEO_CARD_HORIZONTAL_BORDER_PX)
  const previewIntrinsicHeight = getPreviewIntrinsicHeight(previewIntrinsicWidth, cssAspectRatio)
  // Always reserve the QuickInfoBar footer height, even while the spinner shows,
  // so a rendering card is the same height as a completed one (post 1539881217769176).
  // Full-chrome card: reserve unless chrome isn't rendered at all.
  const quickInfoIntrinsicHeight =
    !shouldRenderNodeChrome ? 0 : CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT
  // Lightweight card: reserve the footer space in every state too, so a
  // rendering lightweight card matches a completed one (the footer itself is
  // still only shown on hover).
  const lightweightQuickInfoReserveHeight = CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT
  const runningMessage = useMemo(() => {
    if (progressMessage) return progressMessage
    return t(runningPresentation.messageKey)
  }, [progressMessage, runningPresentation.messageKey, t])
  const loadingMessage = isGenerating
    ? runningMessage
    : isPendingCost
      ? t('imageGeneratorNode.waitingForCostApproval')
      : t('videoGeneratorNode.queuedWaiting')
  const metaLoading = useMemo<MetaLoadingDescriptor | undefined>(() => {
    if (!showSpinner) return undefined
    return {
      message: loadingMessage,
      nodeType: 'videoGenerator',
      loadingState: isPendingCost
        ? 'pending-cost'
        : isPendingExecution
          ? 'pending-execution'
          : generationGrace.isLocallyGenerating && !isRunning
            ? 'local-grace'
            : 'running',
    }
  }, [generationGrace.isLocallyGenerating, isPendingCost, isPendingExecution, isRunning, loadingMessage, showSpinner])
  const cancelDuringGraceButton = generationGrace.isWaiting && !isRunning ? (
    <button
      onClick={(event) => {
        event.stopPropagation()
        generationGrace.cancelPending()
      }}
      className="nodrag mt-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      data-testid="cancel-node-button"
      title={t('videoGeneratorNode.stop')}
    >
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="6" width="12" height="12" rx="1" />
      </svg>
      <span className="text-xxs font-medium">{t('videoGeneratorNode.stop')}</span>
    </button>
  ) : null

  // Post 1622046606219303: the running overlay used to promise "This may take up to
  // 5 minutes" on every model. Seedance 2.5 and other BYOM video models routinely
  // run 20+ minutes, and BYOM endpoints are user-owned so their duration is
  // unknowable to us — there is no honest number to print.
  //
  // The event is named for what is observed — the running overlay rendered — not
  // for the conclusion. A name like "...-hint-removed" would print "removed" on
  // every render whether or not the hint had regressed, so nothing in its own
  // payload could contradict it. Here, "the overlay never rendered" produces no
  // record at all, which is distinguishable from a record saying the overlay
  // rendered with no hint.
  useEffect(() => {
    if (!isVideoRenderWork) return
    videoGenerationWaitHintLog({
      event: 'video-wait-hint-surface-rendered',
      node_id: id,
      node_type: 'video_generator',
      node_status: status,
      wait_hint_rendered: false,
    })
  }, [id, isVideoRenderWork, status])
  useBottomPanelAutoOpen({
    selected,
    isRunning: isGenerating,
    batchPending,
    setBottomPanelOpen,
    setActiveTab: setBottomPanelTab,
    defaultTab: 'config',
  })

  // Reference selector state
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const handleOpenRefSelector = useCallback((event?: { stopPropagation: () => void }) => {
    event?.stopPropagation()
    // Until the delayed request reaches the backend, changing an edge would
    // change the inputs that this already-scheduled generation resolves.
    if (generationGrace.isLocallyGenerating) return
    setIsRefSelectorOpen(true)
  }, [generationGrace.isLocallyGenerating])

  const currentRefs = useMemo<SelectedReference[]>(() => resolveCharacterOwnedAudioReferenceLabels(
    canvasNodes, edges, buildOrderedReferenceMedia(canvasNodes, edges, id, [
      ['in_images', 'image'], ['in_reference_videos', 'video'], ['in_reference_audios', 'audio'],
    ]),
  ), [canvasNodes, edges, id])
  const displayedReferenceOrder = currentRefs.map(referenceMediaKey).join(' -> ')
  useEffect(() => {
    referenceMediaOrderLog({ event: 'reference-media:displayed',
      trace_id: `reference-media:${projectId}:${id}`, project_id: projectId,
      node_id: id, node_type: 'video_generator',
      reference_key_format: 'media:source_node:source_port:array_index (empty = whole output)',
      ordered_references: displayedReferenceOrder })
  }, [id, projectId, displayedReferenceOrder])

  const currentImageRefs = useMemo(() => currentRefs.filter(ref => ref.mediaType === 'image'), [currentRefs])
  const currentVideoRefs = useMemo(() => currentRefs.filter(ref => ref.mediaType === 'video'), [currentRefs])
  const currentAudioRefs = useMemo(() => currentRefs.filter(ref => ref.mediaType === 'audio'), [currentRefs])

  useEffect(() => {
    if (!videoAudioReferenceVisibilityLog.enabled) return
    videoAudioReferenceVisibilityLog({
      event: 'video-audio-reference-visibility:resolved',
      trace_id: `video-audio-reference-visibility:${id}`,
      node_id: id,
      model,
      image_reference_count: currentImageRefs.length,
      video_reference_count: currentVideoRefs.length,
      audio_reference_count: currentAudioRefs.length,
      voice_profile_edge_count: voiceProfileEdges.length,
      audio_reference_node_ids: currentAudioRefs.map((reference) => reference.nodeId),
    })
  }, [currentAudioRefs, currentImageRefs, currentVideoRefs, id, model, voiceProfileEdges.length])

  // BYOM reference support is declared per-registration, so the reference
  // policy needs the selected model's capabilities — not just its id.
  const modelCapabilities = useMemo(
    () => videoModelsReady
      ? byomModels.find((entry) => entry.id === model)?.capabilities ?? null
      : null,
    [byomModels, model, videoModelsReady],
  )
  const currentCharacterVoiceRefs = useMemo(
    () => characterReferencesWithActiveVoice(currentImageRefs, canvasNodes, edges),
    [canvasNodes, currentImageRefs, edges],
  )
  const characterVoiceReferenceWarning =
    shouldWarnCharacterVoiceReferenceUnsupported(
      currentCharacterVoiceRefs.length,
      model,
      modelCapabilities,
    )
      ? t('videoGeneratorNode.characterVoiceReferenceUnsupportedWarning', {
          model: getVideoModelDisplayName(model, byomModels),
        })
      : undefined
  const referenceVideoBlockReasonCode = useMemo(
    () =>
      getReferenceVideoBlockReason(
        model,
        currentVideoRefs,
        referenceVideoEdits,
        getVideoModelDisplayName(model, byomModels),
        modelCapabilities,
      ),
    [byomModels, currentVideoRefs, model, modelCapabilities, referenceVideoEdits],
  )
  const referenceSelectorVisibleTabs = useMemo(
    () => getVideoGeneratorReferenceSelectorTabs(model, modelCapabilities),
    [model, modelCapabilities],
  )
  const referenceSelectorUploadMediaTypes = useMemo<MediaType[]>(
    () => referenceSelectorVisibleTabs,
    [referenceSelectorVisibleTabs],
  )
  const canSelectReferences = referenceSelectorVisibleTabs.length > 0
  const referenceSelectorLimits = useMemo(
    () => getVideoGeneratorReferenceLimits(model, modelCapabilities),
    [model, modelCapabilities],
  )
  // TOTAL cap across media types. Known native models preserve their declared
  // combined cap, including explicit null for unlimited. BYOM has no combined-
  // cap field, so fully declared per-media maxima are added together (50
  // images + 3 video + 3 audio => 56); partially undeclared BYOM stays
  // fail-open at the selector.
  const referenceSelectorTotalLimit = useMemo(
    () => getReferenceSelectorTotalLimit(
      model,
      modelCapabilities,
      DEFAULT_REFERENCE_TOTAL_LIMIT,
    ),
    [model, modelCapabilities],
  )
  const referenceOrder = useMemo(() => buildReferenceMediaOrder(canvasNodes, edges, id), [canvasNodes, edges, id])
  const contractBlockReasons = useMemo(() => isLocalMotionImage ? [] : preflight({
    modelId: model, byomCapabilities: modelCapabilities, referenceOrder,
    inputCounts: {
      in_images: currentImageRefs.length,
      in_reference_videos: currentVideoRefs.length,
      in_reference_audios: currentAudioRefs.length,
    },
    parameters: { aspect_ratio, resolution, duration, reference_mode: referenceMode && currentImageRefs.length > 0 },
  }, getVideoModelDisplayName(model, byomModels), "console-video"), [model, modelCapabilities, referenceOrder, currentImageRefs.length,
    currentVideoRefs.length, currentAudioRefs.length, aspect_ratio, resolution, duration, referenceMode, isLocalMotionImage, byomModels])
  const videoBlockReasonCode = motionGenerateBlockReasonCode ?? contractBlockReasons[0] ?? referenceVideoBlockReasonCode
  const videoCanGenerate = motionCanGenerate && !videoBlockReasonCode
  const videoGenerateBlockReason = videoBlockReasonCode
    ? t(`generation.blockReason.${videoBlockReasonCode.code}`, videoBlockReasonCode.params)
    : undefined

  const effectiveBottomPanelTab = activeVideoGeneratorBottomPanelTab(bottomPanelTab)
  // ── Variants panel (dedicated, tab-less, canvas-zoom) — post 1533007285123236 ──
  const variantsMode = effectiveBottomPanelTab === 'variants'
  // Grid base aspect: true-aspect (clampPortrait=false) so 9:16 variant tiles
  // match the original preview. Computed once and fed to BOTH the layout helper
  // and the VideoVariantsTabContent render so panel size and grid never diverge.
  const videoGridAspect = useMemo(
    () => resolveVideoGridAspect(historyEntries, cssAspectRatio, { clampPortrait: false }),
    [historyEntries, cssAspectRatio],
  )
  const variantLayout = useMemo(
    () =>
      computeVariantPanelLayout(
        videoGridAspect,
        nodeWidth,
        historyEntries.length + batchPending,
      ),
    [videoGridAspect, nodeWidth, historyEntries.length, batchPending],
  )
  const videoModelsChecking = !videoModelsReady
  const isVideoModelSelectable = videoModelsChecking || isSelectableVideoModel(model, nativeModels, byomModels)
  const videoModelUnavailableReason = !videoModelsChecking && !isVideoModelSelectable
    ? unavailableVideoModelReason(model, t)
    : undefined
  // Node card shows the model name only, like the other node cards; aspect
  // ratio and resolution live in the config panel's summary bar.
  const configSummary = videoModelUnavailableReason
    ? 'Model unavailable · select model'
    : getVideoModelDisplayName(model, byomModels)

  // Post 1586850233072274 — "Dead-End 'Generation Timed Out' Error Loops".
  // The single, deduplicated "generation is blocked right now, and here is what
  // to do about it" string. Previously each call site re-derived
  // `videoModelUnavailableReason ?? videoGenerateBlockReason` inline and used it
  // ONLY as a tooltip on the already-disabled generate control, so the node's
  // red banner kept showing a STALE error (e.g. the generic "Generation timed
  // out. Please try again." that `sanitizeNodeError` collapses every timeout
  // into) while a deterministic, user-fixable validation was the real blocker.
  // Users read "try again", retried, and got nowhere — the reported dead end.
  const generateBlockReason = videoModelUnavailableReason ?? videoGenerateBlockReason

  // What the error banner should actually say. Resolution lives in the pure
  // `resolveVideoBanner` helper so the precedence rule is unit-testable
  // (`__tests__/videoBanner.test.ts`) rather than buried in a 6000-line component.
  const banner = useMemo(
    () => resolveVideoBanner({ blockReason: generateBlockReason, status, error, t }),
    [generateBlockReason, status, error, t],
  )
  const bannerMessage = banner.message
  const bannerTooltip = banner.tooltip

  // The "model isn't available" notice is dismissable: it stays true until the
  // user picks another model, so it would otherwise sit over the preview for
  // good. Dismissal lasts until the banner text changes.
  const [dismissedBannerMessage, setDismissedBannerMessage] = useState<string | null>(null)
  const bannerDismissable = bannerMessage !== undefined && bannerMessage === videoModelUnavailableReason
  const bannerDismissed = bannerDismissable && dismissedBannerMessage === bannerMessage
  useEffect(() => {
    if (dismissedBannerMessage !== null && bannerMessage !== dismissedBannerMessage) {
      setDismissedBannerMessage(null)
    }
  }, [bannerMessage, dismissedBannerMessage])

  // The card-surface status message overlays the preview (post
  // 1634117708345526), so it reserves no height: an error or block reason never
  // changes the card's outline size.
  const showStatusOverlay = Boolean(bannerMessage) && !showSpinner && !bannerDismissed
  const statusTone = banner.source === 'block-reason' ? 'warning' : 'error'
  const cardIntrinsicHeight = previewIntrinsicHeight + quickInfoIntrinsicHeight
  const cardIntrinsicStyle = getCanvasNodeCardIntrinsicStyle(nodeWidth, cardIntrinsicHeight)

  useEffect(() => {
    canvasNodeIntrinsicSizeLog({
      event: 'video-card-intrinsic-size',
      node_id: id,
      node_type: 'videoGenerator',
      width: Math.round(nodeWidth),
      height: Math.round(cardIntrinsicHeight),
      preview_width: Math.round(previewIntrinsicWidth),
      preview_height: Math.round(previewIntrinsicHeight),
      quick_info_height: quickInfoIntrinsicHeight,
      status_overlay: showStatusOverlay,
      aspect_ratio,
      css_aspect_ratio: cssAspectRatio,
      has_asset_dimensions: Boolean(assetDisplayDimensions),
      show_spinner: showSpinner,
    })
  }, [
    aspect_ratio,
    assetDisplayDimensions,
    cardIntrinsicHeight,
    cssAspectRatio,
    id,
    nodeWidth,
    previewIntrinsicHeight,
    previewIntrinsicWidth,
    quickInfoIntrinsicHeight,
    showSpinner,
    showStatusOverlay,
  ])

  useEffect(() => {
    videoBlockReasonBannerLog({
      event: 'banner:resolved',
      trace_id: `video-generate-block-reason:${id}`,
      node_id: id,
      node_status: status,
      // Which source won the banner. `block-reason` is the fix's whole point:
      // a current, actionable blocker beat whatever the last run left behind.
      banner_source: banner.source,
      block_reason_code: videoBlockReasonCode ?? null,
      model_unavailable: Boolean(videoModelUnavailableReason),
      // True exactly in the reported dead-end state: a stale error WOULD have
      // been shown, but an actionable blocker superseded it.
      superseded_stale_error: banner.supersededStaleError,
      superseded_error_text: banner.supersededErrorText ?? null,
      banner_message: banner.message ?? null,
      can_generate: videoCanGenerate && !videoModelUnavailableReason,
    })
  }, [
    id,
    status,
    banner,
    videoBlockReasonCode,
    videoModelUnavailableReason,
    videoCanGenerate,
  ])

  useEffect(() => {
    videoModelValidationLog({
      event: 'model:availability',
      trace_id: `video-model-validation:${id}`,
      node_id: id,
      model,
      validity: videoModelsChecking ? 'checking' : isVideoModelSelectable ? 'valid' : 'invalid',
      native_count: nativeModels.length,
      byom_count: byomModels.length,
    })
  }, [byomModels.length, id, isVideoModelSelectable, model, nativeModels.length, videoModelsChecking])

  const openVideoPanelToTab = useCallback(
    (tab: string) => {
      const nextTab = activeVideoGeneratorBottomPanelTab(tab)
      if (generationGrace.isLocallyGenerating && nextTab === VIDEO_GENERATOR_CONFIG_TAB_ID) return
      setBottomPanelTab(nextTab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'videoGenerator',
        mode: nextTab === 'variants' ? 'variants' : 'config',
        active_tab: nextTab,
      })
    },
    [generationGrace.isLocallyGenerating, id, setNodes],
  )

  const handleRefSave = useCallback(
    (refs: SelectedReference[], enforceModelLimits = true) => {
      if (!generationGrace.isLocallyGenerating && canvas) {
        const imageRefs = refs.filter((ref) => ref.mediaType === 'image')
        // Truncate to what the SELECTED model supports rather than a fixed 1
        // (Omni's cap). `null` means no Dreamwrite cap — keep every pick.
        const videoRefLimit = getReferenceVideoLimit(model, modelCapabilities)
        const pickedVideoRefs = refs.filter((ref) => ref.mediaType === 'video')
        const videoRefs =
          !enforceModelLimits || videoRefLimit === null ? pickedVideoRefs : pickedVideoRefs.slice(0, videoRefLimit)
        // Trim windows are no longer authored in the console. Models do not take
        // a start/end — Dreamwrite was cutting the file with ffmpeg — so an
        // over-long clip is REFUSED by the agent, which probes the real
        // duration. Clearing the field (rather than leaving it) matters: a
        // stale window from before that change would still drive the trim path.
        //
        // Audio is capped the way video is: to what the SELECTED model
        // declares. Without this write the Audio tab would accept a selection
        // and silently discard it on save.
        // Shot List packs own their screenplay-scoped voice edges. A no-op
        // reference Save must not replace them with every visible character.
        const isShotListPack = Boolean(node?.get('in_shot_list_source_id'))
        const characterVoiceRefs = isShotListPack ? [] : characterReferencesWithActiveVoice(imageRefs, canvasNodes, edges)
        const characterVoiceAudioRefs = isShotListPack ? [] : buildCharacterVoiceAudioReferences(imageRefs, canvasNodes, edges)
        const audioRefLimit = getReferenceAudioLimit(model, modelCapabilities)
        const pickedAudioRefs = (() => {
          const seenAudioRefs = new Set<string>()
          return [...refs.filter((ref) => ref.mediaType === 'audio'), ...characterVoiceAudioRefs]
            .filter((ref) => {
              const key = referenceMediaKey(ref)
              if (seenAudioRefs.has(key)) return false
              seenAudioRefs.add(key)
              return true
            })
        })()
        const audioRefs =
          !enforceModelLimits || audioRefLimit === null ? pickedAudioRefs : pickedAudioRefs.slice(0, audioRefLimit)
        const accepted = new Set([...imageRefs, ...videoRefs, ...audioRefs])
        const seenOrderedRefs = new Set<string>()
        const ordered = [...refs, ...characterVoiceAudioRefs].filter((ref) => {
          if (!accepted.has(ref)) return false
          const key = referenceMediaKey(ref)
          if (seenOrderedRefs.has(key)) return false
          seenOrderedRefs.add(key)
          return true
        })
        replaceReferenceMediaEdges(canvas, id, ordered, ['in_images', 'in_reference_videos', 'in_reference_audios'])
        referenceMediaOrderLog({ event: 'reference-media:edges-updated', node_id: id, node_type: 'video_generator',
          trace_id: `reference-media:${projectId}:${id}`, project_id: projectId,
          target_port: 'in_reference_media',
          ordered_references: ordered.map(referenceMediaKey).join(' -> ') })
        if (!isShotListPack) {
          canvas.replacePortEdges(
            id,
            'in_voice_profiles',
            mergeCharacterVoiceReferenceEdgeUpdates(characterVoiceRefs, edges, id),
          )
        }
        canvas.updateNodeData({ id, type: 'videoGenerator' }, { in_reference_video_edits: [] })
        videoReferenceEditingLog({
          event: 'reference-save',
          trace_id: `video-reference-editing:${id}`,
          node_id: id,
          image_refs: imageRefs.length,
          video_refs: videoRefs.length,
          audio_refs: audioRefs.length,
          paired_character_voice_refs: characterVoiceRefs.length,
          paired_character_voice_audio_refs: characterVoiceAudioRefs.length,
          character_voice_reference_warning: characterVoiceReferenceWarning ?? null,
        })
      }

      setIsRefSelectorOpen(false)
    },
    // `model` and `modelCapabilities` are load-bearing here, not incidental:
    // the truncation cap and the trim seed are both read from the SELECTED
    // model. Omitting them pinned the callback to whichever model was current
    // when it was last memoized, so after a switch Omni→BYOM(3) it still sliced
    // to 1, and BYOM→Veo(0) kept references that model rejects.
    [
      canvas,
      canvasNodes,
      characterVoiceReferenceWarning,
      edges,
      id,
      projectId,
      model,
      modelCapabilities,
      node,
      generationGrace.isLocallyGenerating,
    ],
  )

  const historyPaths = historyEntries.map((entry, index) => videoHistoryPath(entry) ?? `missing:${index}`)
  const historyPathsSignature = historyPaths.join('|')
  const historyPathsJson = JSON.stringify(historyPaths)
  const renderReason = isGenerating
    ? 'run_in_progress'
    : historyEntries.length > 1
        ? 'history_resolved'
        : 'baseline'

  useEffect(() => {
    videoVariantSyncLog({
      event: 'video-variant-sync:render_state',
      trace_id: `video-variant-sync:${id}`,
      node_id: id,
      render_reason: renderReason,
      status,
      history_count: historyEntries.length,
      selected_history_index: pickedIndex,
      selected_variant_path: videoPath ?? null,
      ydoc_out_video_path: videoPath ?? null,
      history_paths_json: historyPathsJson,
    })
  }, [historyEntries.length, historyPathsJson, historyPathsSignature, id, pickedIndex, renderReason, status, videoPath])

  useEffect(() => {
    setAssetDisplayDimensions(null)
  }, [videoPath, pickedIndex])

  const baseVideoUrl =
    videoPath && projectId
      ? getAssetUrl(projectId, videoPath, 0, cacheKey)
      : null

  const videoUrl = useAuthenticatedMediaUrl(baseVideoUrl)
  const renderFullCard = !lightweightNodeChrome || !!selected || !!dragging || isHoveringLightweightNodeChrome || isVideoModalOpen
  useEffect(() => {
    if (!previewHoverLog.enabled) return
    previewHoverLog({
      event: 'video-preview-hover:resolved',
      feature: 'canvas',
      node_id: id,
      lightweight: lightweightNodeChrome,
      host_hovered: isHoveringLightweightNodeChrome,
      preview_area_hovered: isHovering,
      preview_hovered: isPreviewHovering,
      selected: !!selected,
      player_rendered: Boolean(videoUrl) && !showSpinner && renderFullCard,
    })
  }, [id, lightweightNodeChrome, isHoveringLightweightNodeChrome, isHovering, isPreviewHovering, selected, renderFullCard, videoUrl, showSpinner])
  const cardOverlay = resolveVideoCardOverlay({
    showSpinner,
    hasVideo: Boolean(videoUrl),
    downloadPreparing,
    bannerMessage,
    bannerDismissed,
  })

  // First-frame poster URL (RFC-0931) — lets the idle card / preview render a
  // cheap <img> instead of a <video>. Null for absolute upload URLs (no
  // backend poster sibling) → those keep the <video> fallback.
  const baseVideoPosterUrl =
    videoPath && projectId ? getVideoPosterUrl(projectId, videoPath, cacheKey) : null
  const videoPosterUrl = useAuthenticatedMediaUrl(baseVideoPosterUrl)

  // Build video URL for bottom panel cells
  const buildVideoUrl = useCallback(
    (localPath: string): string | null => {
      if (!projectId) return null
      return getAssetUrl(projectId, localPath)
    },
    [projectId],
  )

  // First-frame poster URL per variant cell (RFC-0931) so the grid renders a
  // cheap <img> at idle instead of mounting a <video> per cell.
  const buildPosterUrl = useCallback(
    (localPath: string): string | null => {
      if (!projectId) return null
      return getVideoPosterUrl(projectId, localPath)
    },
    [projectId],
  )

  // Round 4 items 4 + 5 + 6: route preset clicks to the right target.
  //  - Freeform editor open  → insertAtCursor on the freeform RichTextBox.
  //  - Structured block editor open → insertAtCursor on that block's body
  //    RichTextBox (via formattedMotionPromptRef.getActiveBodyRef()).
  //  - Structured mode, no editor open → append a new video block whose body
  //    is the preset; the new editor auto-opens as a local draft.
  // Every path snapshots the current prompt onto prevPromptStackRef so Cmd+Z
  // can revert (item 6). Item 5 is enforced inside CameraCommandsPanel which
  // now only inserts `[Preset] ` and no `[End of …]` closing tag.
  const updateConfigs = useCallback(
    (updates: VideoGeneratorConfigUpdates) => {
      // YDoc-first: route the write through canvas.updateNodeData, which does the
      // controlled-state ReactFlow update (display) AND the YDoc write
      // (persistence). Do NOT also write to ReactFlow's internal store via
      // useReactFlow().setNodes — in a controlled canvas that emits a `reset`
      // change which can re-insert a just-deleted node, causing the video
      // generator delete flicker / "delete twice" bug (T276953451).
      if (!canvas) {
        // VideoGeneratorNode only mounts inside the canvas provider in the app;
        // a missing canvas is a test-only render. NAD-log it (our loud signal)
        // and skip — matches the prior `canvas?.updateNodeData` tolerance. We do
        // NOT fall back to useReactFlow().setNodes here (that internal-store
        // write is the delete-flicker root cause, T276953451).
        videoConfigWriteLog({
          event: 'video-config-write:no-canvas',
          trace_id: `video-config-write:${id}`,
          node_id: id,
          fields: Object.keys(updates).join(','),
        })
        return
      }
      videoConfigWriteLog({
        event: 'video-config-write:ydoc-first',
        trace_id: `video-config-write:${id}`,
        node_id: id,
        fields: Object.keys(updates).join(','),
      })
      canvas.updateNodeData({ id, type: 'videoGenerator' }, updates)
    },
    [id, canvas],
  )

  // Update config
  const updateConfig = useCallback(
    <K extends VideoGeneratorConfigUpdateKey,>(key: K, value: VideoGeneratorNodeData[K]) => {
      updateConfigs({ [key]: value })
    },
    [updateConfigs],
  )

  const handleModelChange = useCallback(
    (nextModel: string) => {
      if (nextModel === model) return
      if (isByomModel(nextModel) && !videoModelsReady) {
        videoModelCatalogLog({
          event: 'model-selection:blocked-catalog-not-ready',
          trace_id: `video-model-catalog:${id}:selection`,
          node_id: id,
          previous_model: model,
          selected_model: nextModel,
        })
        return
      }
      const byomCapabilities = isByomModel(nextModel)
        ? byomModels.find((entry) => entry.id === nextModel)?.capabilities ?? null
        : undefined
      const snapped = snapOnModelChange(
        model,
        nextModel,
        { duration, resolution },
        byomCapabilities,
      )
      const updates: VideoGeneratorConfigUpdates = { in_model: nextModel }
      if (snapped.duration !== duration) updates.in_duration = snapped.duration
      if (snapped.resolution !== resolution) updates.in_resolution = snapped.resolution
      if (
        [
          'seedance-2.5-image-to-video-atlas-cloud',
          'seedance-2.5-first-last-frame-atlas-cloud',
          'seedance-2.5-video-editing-atlas-cloud',
          'seedance-2.5-video-extension-atlas-cloud',
          // Omni 1.1 Edit/Extend take their output ratio from the source
          // video — Vertex rejects `aspect_ratio` outright for both subtasks.
          'gemini-omni-1.1-flash-edit',
          'gemini-omni-1.1-flash-extend',
        ].includes(nextModel)
        && aspect_ratio !== 'adaptive'
      ) {
        updates.in_aspect_ratio = 'adaptive'
      }
      const snappedMode = modeOnModelChange(nextModel)
      if (snappedMode !== undefined) updates.in_mode = snappedMode
      if (
        (nextModel === 'minimax-h3-reference-to-video'
          || nextModel === 'minimax-h3-reference-to-video-atlas-cloud')
        && !['adaptive', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'].includes(aspect_ratio)
      ) {
        updates.in_aspect_ratio = '16:9'
      } else if (
        (nextModel === 'mango-video-text-to-video'
          || nextModel === 'mango-video-image-to-video')
        && aspect_ratio !== '16:9'
      ) {
        updates.in_aspect_ratio = '16:9'
      }
      // General fallback for every model the hand-written rules above do not
      // name. It is what carries a node OUT of an adaptive-only model: the
      // rules above only ever force 'adaptive' ON, so without this, switching
      // Omni 1.1 Edit (or Seedance 2.5 Edit/Extend) back to a ratio-bound
      // model left 'adaptive' persisted — the flyout then showed that model's
      // ratios with none selected while the request still sent a ratio the
      // model does not declare. Reads the generated capability record, so it
      // needs no per-model list of its own.
      if (updates.in_aspect_ratio === undefined) {
        const snappedAspectRatio = snapAspectRatioOnModelChange(nextModel, aspect_ratio)
        if (snappedAspectRatio !== undefined) {
          updates.in_aspect_ratio = snappedAspectRatio
        }
      }

      videoModelCatalogLog({
        event: byomCapabilities === null
          ? 'model-selection:capabilities-unavailable'
          : 'model-selection:normalized',
        trace_id: `video-model-catalog:${id}:selection`,
        node_id: id,
        previous_model: model,
        selected_model: nextModel,
        previous_duration: duration,
        next_duration: snapped.duration,
        previous_resolution: resolution,
        next_resolution: snapped.resolution,
      })
      updateConfigs(updates)
    },
    [aspect_ratio, byomModels, duration, id, model, resolution, updateConfigs, videoModelsReady],
  )

  const handleResolutionChange = useCallback(
    (nextResolution: string) => {
      const nextDuration = snapOnResolutionChange(model, nextResolution, duration)
      const updates: VideoGeneratorConfigUpdates = { in_resolution: nextResolution }
      if (nextDuration !== duration) updates.in_duration = nextDuration
      updateConfigs(updates)
    },
    [duration, model, updateConfigs],
  )

  const handleBottomPanelTabChange = useCallback(
    (tab: string) => {
      const nextTab = activeVideoGeneratorBottomPanelTab(tab)
      if (generationGrace.isLocallyGenerating && nextTab === VIDEO_GENERATOR_CONFIG_TAB_ID) return
      setBottomPanelTab(nextTab)
    },
    [generationGrace.isLocallyGenerating],
  )

  useEffect(() => {
    if (!generationGrace.isLocallyGenerating) return
    setIsRefSelectorOpen(false)
    if (bottomPanelTab === 'config' && bottomPanelOpen && selected) {
      videoConfigDispatchGuardLog({
        event: 'config-panel:waiting-for-dispatch',
        node_id: id,
        phase: generationGrace.phase,
      })
    }
  }, [
    bottomPanelOpen,
    bottomPanelTab,
    generationGrace.isLocallyGenerating,
    generationGrace.phase,
    id,
    selected,
  ])

  // Round 4 items 4 + 5 + 6: handler used by CameraCommandsPanel. See the
  // big comment above `updateConfigs` for full routing rules.
  const handlePresetApply = useCallback(
    (preset: string) => {
      const snapshot = isEditingPromptRef.current ? localPromptRef.current : prompt
      prevPromptStackRef.current.push(snapshot)
      if (prevPromptStackRef.current.length > 50) prevPromptStackRef.current.shift()

      if (isEditingPromptRef.current) {
        avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'freeform', target: 'freeform', presetId: preset })
        richTextBoxRef.current?.insertAtCursor(`[${preset}] `)
        return
      }

      const activeBody = formattedMotionPromptRef.current?.getActiveBodyRef()
      if (activeBody) {
        avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'structured', target: 'editor', presetId: preset })
        activeBody.insertAtCursor(`[${preset}] `)
        return
      }

      avScriptLog({ event: 'preset-apply', nodeId: id, mode: 'structured', target: 'new-block', presetId: preset })
      formattedMotionPromptRef.current?.appendVideoBlockFromPreset(preset)
    },
    [id, prompt],
  )

  // Round 4 item 6 / Round 5 bug 2: Cmd+Z reverts the last preset insertion
  // whenever this node is the active selection. Previously gated on
  // `bottomPanelOpen`, which is only true after Audio Remix save — so the
  // listener never installed for normal Config-tab preset use. Scope via
  // `selected` so the shortcut only fires for the currently focused node.
  useEffect(() => {
    if (!selected) {
      if (prevPromptStackRef.current.length > 0) {
        avScriptLog({
          event: 'preset-undo-skip-out-of-scope',
          nodeId: id,
          stackLen: prevPromptStackRef.current.length,
        })
      }
      return
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'KeyZ') return
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.shiftKey) return
      if (prevPromptStackRef.current.length === 0) return
      const prev = prevPromptStackRef.current.pop()
      if (prev === undefined) return
      e.preventDefault()
      e.stopPropagation()
      avScriptLog({ event: 'preset-undo', nodeId: id, restoredLen: prev.length })
      if (isEditingPromptRef.current) {
        setLocalPrompt(prev)
      }
      updateConfig('in_motion_prompt', prev)
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [selected, id, updateConfig])

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      clipLabelLog({ event: 'clip-label-rename', node_id: id, old_name: in_name ?? '', new_name: newName })
      updateConfig('in_name', newName)
    },
    [id, in_name, updateConfig],
  )

  // Trace the clip label payload so `scripts/nad trace --feature video-clip-label`
  // shows whether the FloatingNodeLabel is rendering with a non-empty name.
  useEffect(() => {
    clipLabelLog({
      event: 'clip-label-render',
      node_id: id,
      in_name: in_name ?? '',
      label: label ?? '',
      effective_name: displayName || '(empty)',
    })
  }, [displayName, id, in_name, label])

  // Generate handler — useClickLock prevents duplicate client commands while
  // the grace-period store owns the durable 3-second pre-submit window.
  const showInsufficientSparks = useShowInsufficientSparks()
  const { isLocked: isGenerateLockedKey, run: runGenerate } = useClickLock(1000)
  const generateLocked = isGenerateLockedKey(id)

  useEffect(() => {
    const failure = generationGrace.failure
    if (!failure) return
    generationGrace.clearFailure()
    const failureMessage = failure.code === 'submission_unconfirmed'
      ? t('generationGracePeriod.submissionUnconfirmed')
      : failure.message
    if (
      failure.code === 'insufficient_sparks' &&
      projectId
    ) {
      showInsufficientSparks({
        nodeId: id,
        nodeType: 'video_generator',
        projectId,
      })
      return
    }
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, status: 'error', error: failureMessage } }
          : n,
      ),
    )
  }, [generationGrace, id, projectId, setNodes, showInsufficientSparks, t])

  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    if (showSpinner) return

    if (videoModelUnavailableReason) {
      videoModelValidationLog({
        event: 'model:generate-blocked',
        trace_id: `video-model-validation:${id}`,
        node_id: id,
        model,
        reason: videoModelUnavailableReason,
      })
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: videoModelUnavailableReason } }
            : n,
        ),
      )
      return
    }

    // Auto-save unsaved motion prompt edits before generating
    if (isEditingPromptRef.current) {
      logMotionPromptSave(id, 'generate', localPromptRef.current)
      updateConfig('in_motion_prompt', localPromptRef.current)
      setIsEditingPrompt(false)
    }

    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n,
        ),
      )
      return
    }

    void runGenerate(id, async () => {
      try {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
              : n,
          ),
        )
        generationGrace.schedule(retryCount, {
          tab: 'config',
          generationMode: generateModeFor(historyEntries.length > 0),
          referenceCount: currentRefs.length,
          modelSource: modelSourceOf(model),
        })
      } catch (err) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n,
          ),
        )
      }
    })
  }, [generationGrace, id, model, projectId, runGenerate, setNodes, showSpinner, updateConfig, videoModelUnavailableReason, historyEntries.length, currentRefs.length])

  // Download handler — follows the picker so users get the variant they selected.
  const downloadPath = useMemo(() => {
    const selectedHistoryPath = videoHistoryPath(historyEntries[pickedIndex])
    if (selectedHistoryPath) return selectedHistoryPath
    return undefined
  }, [historyEntries, pickedIndex])

  const onDownload = useCallback(() => {
    if (!downloadPath) return
    const { filename } = buildClipDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: edges,
      nodeId: id,
      variantHistory: historyEntries,
      pickedIndex,
      assetPath: downloadPath,
      trigger: 'node-header',
    })
    downloadFile(downloadPath, filename)
  }, [downloadPath, downloadFile, getNodes, edges, id, historyEntries, pickedIndex])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(downloadPath))

  const extractFrameAction = useExtractFrameAction({
    nodeId: id,
    hasVideo: Boolean(videoPath),
    videoPreviewRef,
  })
  const extractAudioAction = useExtractAudioAction({
    nodeId: id,
    sourceNodeType: 'video_generator',
    hasVideo: Boolean(videoPath),
    onRunningModeChange: setExtractAudioRunningMode,
  })

  // Handle history selection
  const handleHistorySelect = useCallback(
    // Returns whether the target variant is now the picked one — false when the
    // index is out of range or the entry has no local path (a pending/failed
    // variant can't be picked). Callers that need atomicity (RFC-0986 revert)
    // check this; the Variants panel ignores it (a click on a pathless cell is a
    // no-op there too).
    (index: number): boolean => {
      const entry = historyEntries[index]
      const localPath = videoHistoryPath(entry)
      if (!localPath) return false

      const ynode = canvas?.getYNode?.(id)
      if (!ynode) {
        throw new Error(`VideoGeneratorNode ${id}: missing YDoc node for variant selection`)
      }
      const outVideo = ynode.get('out_video')
      if (!(outVideo instanceof Y.Map)) {
        throw new Error(`VideoGeneratorNode ${id}: out_video must be Y.Map for variant selection`)
      }
      const alreadyPicked = outVideo.get('picked_index') === index
      const writePickedIndex = () => {
        if (outVideo.get('picked_index') !== index) {
          outVideo.set('picked_index', index)
        }
        const variantId = entry?.result?.id
        outVideo.set(
          'picked_variant_id',
          typeof variantId === 'string' && variantId ? variantId : null,
        )
      }
      if (ynode.doc) {
        ynode.doc.transact(writePickedIndex, 'variant-selection')
      } else {
        writePickedIndex()
      }

      videoVariantSyncLog({
        event: 'video-variant-sync:variant_selected',
        trace_id: `video-variant-sync:${id}`,
        node_id: id,
        selected_history_index: index,
        selected_variant_path: localPath,
        history_count: historyEntries.length,
      })
      // RFC-0974/1577: `canvas_variant_kept` — only on an actual change of the
      // kept variant. Same variant_id format as the image node.
      if (!alreadyPicked && projectId) {
        trackVariantKeptChange(projectId, id, 'video_generator', index)
      }
      return true
    },
    [id, historyEntries, canvas, projectId],
  )

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      extractFrameAction,
      extractAudioAction,
      depthVideoAction,
      upscaleVideoAction,
      {
        key: 'addToChat',
        label: t('videoGeneratorNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      {
        key: 'duplicate',
        label: t('videoGeneratorNode.duplicate'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('videoGeneratorNode.delete'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [depthVideoAction, upscaleVideoAction, duplicateMenuItems, handleDelete, handleAddToChat, handleSaveToLibrary, saveState, saveLabel, extractAudioAction, extractFrameAction, t],
  )

  const handleRemoveReference = useCallback((ref: SelectedReference) => {
    handleRefSave(currentRefs.filter(candidate => referenceMediaKey(candidate) !== referenceMediaKey(ref)), false)
  }, [currentRefs, handleRefSave])

  // Reference picker for Config tab
  const selectCanvasReferences = useCanvasReferencePicker({
    projectId: projectId ?? undefined,
    ownerNodeId: id,
    references: currentRefs,
    onChange: handleRefSave,
    mediaTypes: referenceSelectorVisibleTabs,
    maxReferences: referenceSelectorTotalLimit,
    mediaLimits: referenceSelectorLimits,
    referenceCandidateFilter: isVideoGeneratorReferenceCandidate,
  })

  // Picking references from the canvas needs the canvas, which the expanded
  // motion prompt popup covers — collapse the popup first.
  const selectCanvasReferencesFromPicker = useCallback(() => {
    setIsPromptExpanded(false)
    selectCanvasReferences()
  }, [selectCanvasReferences])

  const referenceImagePicker = useMemo(() => {
    return (
      <>
        <VideoReferencePicker
          onSelectCanvas={selectCanvasReferencesFromPicker}
          nodeId={id}
          references={currentRefs}
          projectId={projectId}
          cacheKey={cacheKey ?? undefined}
          canSelectReferences={canSelectReferences}
          onOpenSelector={handleOpenRefSelector}
          onRemoveReference={handleRemoveReference}
          onReorderReferences={(refs) => handleRefSave(refs, false)}
        />
        {characterVoiceReferenceWarning && (
          <p
            className="mt-2 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1 text-xxs text-warning"
            data-testid="character-voice-reference-warning"
            role="status"
          >
            {characterVoiceReferenceWarning}
          </p>
        )}
      </>
    )
  }, [
    cacheKey,
    canSelectReferences,
    currentRefs,
    handleOpenRefSelector,
    handleRemoveReference,
    handleRefSave,
    selectCanvasReferencesFromPicker,
    id,
    projectId,
    characterVoiceReferenceWarning,
  ])

  // Build motion prompt + error content for bottom panel.
  // CINEMATOGRAPHY PRESETS are rendered whenever the user is editing the
  // motion prompt — either via the freeform RichTextBox (isEditingPrompt) or
  // via any Structured per-block editor (isStructuredEditing, lifted up from
  // FormattedMotionPrompt). The buttons insert tags into the most-recently
  // focused editor (existing RichTextBox + native textarea behavior).
  const showCinematographyPresets = isEditingPrompt || isStructuredEditing

  // The presets panel appears below the motion prompt only while editing.
  // Measure it so the Config popup grows by that much instead of squeezing
  // the timeline — opening a block editor must not resize the blocks.
  // offsetHeight ignores the panel's zoom transform, matching contentHeight.
  const [presetsHeightPx, setPresetsHeightPx] = useState(0)
  const presetsObserverRef = useRef<ResizeObserver | null>(null)
  const presetsMeasureRef = useCallback((el: HTMLDivElement | null) => {
    presetsObserverRef.current?.disconnect()
    presetsObserverRef.current = null
    if (!el) {
      setPresetsHeightPx(0)
      return
    }
    const update = () => setPresetsHeightPx(el.offsetHeight)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    presetsObserverRef.current = observer
  }, [])
  const motionPromptContent = useMemo(() => {
    return (
      // Fill the Config panel height (flex column) so the prompt body expands
      // to the control bar and no void remains below it — matches the Image
      // Generator's filling prompt textarea (post 1533007285123236).
      <div className="flex flex-col flex-1 min-h-0">
        {isEditingPrompt ? (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between mb-1 shrink-0">
              <label className="text-xs font-medium uppercase tracking-wider text-text-muted">
                {t('videoGeneratorNode.motionPrompt')}
              </label>
              <button
                onClick={() => {
                  logMotionPromptSave(id, 'done', localPrompt)
                  updateConfig('in_motion_prompt', localPrompt)
                  setIsEditingPrompt(false)
                }}
                className="w-5 h-5 rounded hover:bg-surface-1 text-primary/80 hover:text-primary flex items-center justify-center transition-colors"
                title={t('videoGeneratorNode.doneEditing')}
                data-testid="motion-prompt-done-btn"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            </div>
            <CanvasRichTextBox
              ref={richTextBoxRef}
              flexFill
              containerClassName="w-full bg-surface-1 border border-border rounded px-2 py-1.5 nowheel"
              containerTestId="motion-prompt-textarea"
              referenceNodeIds={currentRefs.map(reference => reference.nodeId)}
              referenceOwnerNodeId={id}
              referenceOwnerNodeType="videoGenerator"
              placeholder={t('videoGeneratorNode.motionPromptPlaceholder')}
              initialContent={localPrompt}
              onContentChange={(state: RichTextBoxState) => setLocalPrompt(state.plainText)}
              minHeight={200}
              maxHeight={220}
              maxLength={CREATIVE_TEXT_MAX_LENGTH}
              onSubmit={() => {
                logMotionPromptSave(id, 'submit', localPrompt)
                updateConfig('in_motion_prompt', localPrompt)
                setIsEditingPrompt(false)
              }}
              submitOnEnter={false}
              submitOnShiftEnter
              onEscape={() => {
                setLocalPrompt(prompt)
                setIsEditingPrompt(false)
              }}
            />
          </div>
        ) : (
          <FormattedMotionPrompt
            ref={formattedMotionPromptRef}
            fill
            text={prompt}
            nodeId={id}
            onEdit={() => setIsEditingPrompt(true)}
            onCommitStructured={(newText) => updateConfig('in_motion_prompt', newText)}
            projectId={projectId ?? ''}
            knownSpeakerIndex={knownSpeakerIndex}
            onEditingChange={setIsStructuredEditing}
            expanded={isPromptExpanded}
            onToggleExpanded={togglePromptExpanded}
          />
        )}

        {/* Error Banner — a CURRENT block reason outranks a stale node error, and
            shows even when the node never errored (post 1586850233072274): this
            is the panel the "?" cursor click-through lands on, so it must not
            repeat the same dead-end message the card footer already showed. */}
        {bannerMessage && (
          <div className="shrink-0 mt-2" data-testid="error-banner">
            <NodeStatusMessage
              message={bannerMessage}
              title={bannerTooltip}
              tone={statusTone}
              placement="inline"
            />
          </div>
        )}

        {/* CINEMATOGRAPHY PRESETS — always visible at the bottom of the panel
            whenever ANY motion-prompt editor is active (freeform OR structured
            per-block). Round 4 item 4: the `onInsert` handler routes the
            preset to the right target (freeform editor / open block editor /
            new video block). */}
        {showCinematographyPresets && (
          <div ref={presetsMeasureRef} className="shrink-0 mt-2">
            <CameraCommandsPanel
              richTextBoxRef={richTextBoxRef}
              onInsert={handlePresetApply}
            />
          </div>
        )}
      </div>
    )
  }, [
    currentRefs,
    isEditingPrompt,
    showCinematographyPresets,
    localPrompt,
    updateConfig,
    prompt,
    // The panel's error banner now renders the resolved block-reason/error
    // message, so the memo must recompute when either changes — otherwise the
    // Config tab would keep showing a blocker the user has already fixed.
    // These supersede the previous raw `status` / `error` deps: the banner no
    // longer reads them directly (they are folded into `bannerError`).
    bannerMessage,
    bannerTooltip,
    statusTone,
    projectId,
    id,
    handlePresetApply,
    presetsMeasureRef,
    isPromptExpanded,
    togglePromptExpanded,
    // The prompt panel forwards the connected profile names to the parser, so
    // a newly connected voice profile must re-render it (RFC-1204).
    knownSpeakerIndex,
    t,
  ])

  // While expanded, the Config tab's prompt area (References | Style row +
  // motion prompt) renders inside the popup — moved, not duplicated, so
  // editors, click-away saves and presets keep working — and the tab shows a
  // placeholder in its place.
  const renderPromptArea = useCallback(
    (area: React.ReactNode) =>
      isPromptExpanded ? (
        <>
          <div
            className="flex flex-col flex-1 min-h-0 items-center justify-center gap-2 rounded-lg border border-dashed border-border-subtle text-xs font-medium text-text-muted"
            data-testid="motion-prompt-expanded-placeholder"
          >
            <span>{t('videoGeneratorNode.motionPromptOpenExpanded')}</span>
            <button
              type="button"
              onClick={closePromptExpanded}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs font-medium text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Minimize2 className="w-3.5 h-3.5" aria-hidden />
              {t('videoGeneratorNode.collapseMotionPrompt')}
            </button>
          </div>
          <MotionPromptExpandedModal nodeId={id} onClose={closePromptExpanded}>
            <div className="flex flex-col flex-1 min-h-0 gap-3">{area}</div>
          </MotionPromptExpandedModal>
        </>
      ) : (
        area
      ),
    [isPromptExpanded, closePromptExpanded, id, t],
  )

  if (!renderFullCard) {
    return (
      <div
        data-testid="video-generator-lightweight-host"
        className="w-fit"
        onPointerEnter={() => setIsHoveringLightweightNodeChrome(true)}
        onPointerLeave={() => setIsHoveringLightweightNodeChrome(false)}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="video-generator-flat-node"
          label={displayName}
          fallbackLabel={t('videoGeneratorNode.untitledVideoGenerator')}
          typeIcon={NODE_TYPE_ICON.videoGenerator}
          width={nodeWidth}
          status={generationGrace.isLocallyGenerating || isDialogueExtractionRunning ? 'running' : status}
          error={status === 'error' && error ? sanitizeNodeError(error, t, true) : undefined}
          progressMessage={loadingMessage}
          metaLoading={metaLoading}
          handles={HANDLES}
          mediaKind={videoUrl ? 'video' : 'none'}
          mediaUrl={videoUrl}
          posterUrl={videoPosterUrl}
          outputVariantIndex={pickedIndex}
          aspectRatio={cssAspectRatio}
          placeholderLabel={t('videoGeneratorNode.noVideoYet')}
          footerReserveHeight={lightweightQuickInfoReserveHeight}
        />
      </div>
    )
  }

  return (
    <div
      className="relative"
      onPointerEnter={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(true) : undefined}
      onPointerLeave={lightweightNodeChrome ? () => {
        setIsHoveringLightweightNodeChrome(false)
        // pointerleave precedes mouseleave; the flat-card swap can unmount
        // the preview before its mouseleave handler gets a chance to run.
        setIsHovering(false)
      } : undefined}
    >
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      <FloatingNodeLabel
        name={displayName}
        fallback={t('videoGeneratorNode.untitledVideoGenerator')}
        typeIcon={NODE_TYPE_ICON.videoGenerator}
        onRename={handleRename}
      />


      <div
        className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden shadow transition-all bg-card border group relative ${
          status === 'error'
            ? 'border-destructive'
            : selected
              ? 'ring-2 ring-white/70 border-white/40'
              : 'border-border'
        }`}
        style={{ width: nodePresentationWidth(nodeWidth), ...cardIntrinsicStyle }}
        data-generation-phase={generationGrace.phase ?? undefined}
      >
        {/* Connection Handles */}
        {handlesVisible && HANDLES.map((handle) => (
          <Handle
            key={`${handle.type}-${handle.id}`}
            type={handle.type}
            position={handle.position}
            id={handle.id}
            className={
              handle.position === Position.Top || handle.position === Position.Bottom
                ? '!bg-transparent !border-0 !w-4 !h-1'
                : '!bg-transparent !border-0 !w-1 !h-4'
            }
            style={handle.style}
          />
        ))}

        {/* Preview Area — top corners rounded only; bottom sits flush against QuickInfoBar (post 1579929167097714) */}
        <div
          className="relative w-full bg-canvas overflow-hidden cursor-pointer group rounded-t-lg"
          style={{ aspectRatio: cssAspectRatio, ...previewStyle }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid="video-preview-area"
        >
          {metaLoading ? (
            <MetaLoadingSurface
              {...metaLoading}
              projectId={projectId}
              nodeId={id}
              aspectRatio={cssAspectRatio}
              presentation="full"
            >
              {cancelDuringGraceButton}
            </MetaLoadingSurface>
          ) : videoUrl ? (
            <>
              <VideoPreviewPlayer
                mediaFit={mediaFit}
                ref={videoPreviewRef}
                videoUrl={videoUrl}
                posterUrl={videoPosterUrl}
                isHovering={isPreviewHovering}
                showExpand
                showDownload
                downloadPreparing={downloadPreparing}
                overlayDirection={isPortraitAspectRatio(cssAspectRatio) ? 'column' : 'row'}
                onPreviewSelect={selectThisVideoNode}
                onExpand={() => setIsVideoModalOpen(true)}
                onDownload={onDownload}
                onLoadedDimensions={(width, height) => {
                  setAssetDisplayDimensions(snapNaturalDimensionsToDisplayDimensions(width, height))
                }}
                onLoadedVideoDimensions={(width, height) => {
                  if (videoPath) {
                    setLoadedVideoDimensions({
                      path: videoPath,
                      dimensions: { width, height },
                    })
                  }
                }}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground/20">
              <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xxs">{t('videoGeneratorNode.noVideoYet')}</span>
            </div>
          )}

          {/* Status overlay — floats over the bottom of the preview instead of
              adding a footer (post 1634117708345526). Shows the actionable
              block reason in preference to a stale error (post
              1586850233072274). Suppressed while a render is in flight: an
              in-progress card must not be decorated with the previous run's
              failure. Lifts above the player's scrub/timer row while hovered
              so both stay usable. */}
          {cardOverlay === 'download-preparing' && (
            <NodeDownloadPreparingBanner className={isPreviewHovering ? 'bottom-9' : undefined} />
          )}
          {cardOverlay === 'status' && (
            <NodeStatusMessage
              message={bannerMessage}
              title={bannerTooltip}
              tone={statusTone}
              testId="video-card-error-footer"
              className={videoUrl && isPreviewHovering ? 'bottom-9' : undefined}
              onDismiss={bannerDismissable ? () => setDismissedBannerMessage(bannerMessage ?? null) : undefined}
            />
          )}
        </div>

        {/* Quick Info Bar — Config | Variants. Rendered in every state
            (including while rendering/queued) so the card height matches a
            completed card (post 1539881217769176). Still gated on node chrome
            being rendered (lightweight mode hides it). Brings VideoGenerator to
            parity with ImageGenerator and opens the dedicated Variants panel
            (post 1533007285123236). */}
        {shouldRenderNodeChrome && (
          <QuickInfoBar
            configSummary={configSummary}
            references={currentRefs}
            projectId={projectId}
            cacheBust={cacheKey ?? undefined}
            variantCount={historyEntries.length}
            maxReferences={3}
            onOpenConfig={() => openVideoPanelToTab('config')}
            onOpenVariants={() => openVideoPanelToTab('variants')}
          />
        )}
      </div>

      {/* Bottom Panel */}
      <UnifiedBottomPanel
        contentHeight={
          variantsMode
            ? variantLayout.contentHeight
            : effectiveBottomPanelTab === 'config'
              // + the presets panel (and its 8px top margin) while it shows.
              // Not while expanded: the presets then live in the popup.
              ? 600 + (showCinematographyPresets && !isPromptExpanded && presetsHeightPx > 0 ? presetsHeightPx + 8 : 0)
              : 520
        }
        // The Config tab hosts the sideways-scrolling motion timeline, so it
        // gets a wider popup than the other tabs.
        minWidth={
          variantsMode
            ? variantLayout.minWidth
            : effectiveBottomPanelTab === 'config'
              ? 840
              : 672
        }
        hideTabBar={variantsMode}
        tabs={videoTabs}
        activeTab={effectiveBottomPanelTab}
        onTabChange={handleBottomPanelTabChange}
        isVisible={
          !!selected
          && !dragging
          && bottomPanelOpen
          && !(generationGrace.isLocallyGenerating && effectiveBottomPanelTab === 'config')
        }
      >
        {(tab) => {
          if (tab === 'config') {
            return (
              <VideoConfigTabContent
                nodeId={id}
                model={model}
                onModelChange={handleModelChange}
                currentStyleId={selectedStyleId}
                styleConnected={Boolean(styleEdge)}
                onStyleChange={(styleId) => {
                  videoStyleLog({ event: 'video-style:selected', node_id: id, previous_style_id: selectedStyleId, style_id: styleId })
                  updateConfig('in_style', styleId)
                }}
                referenceImagePicker={referenceImagePicker}
                referenceImageCount={referenceMode ? currentImageRefs.length : 0}
                referenceNotice={
                  showsFirstLastByOrderNotice(model, node?.get('in_mode'), currentImageRefs.length)
                    ? t('generation.notice.firstLastByOrder')
                    : undefined
                }
                motionPromptContent={motionPromptContent}
                renderPromptArea={renderPromptArea}
                duration={duration}
                resolution={resolution}
                aspectRatio={aspect_ratio}
                providerGenerateAudio={providerGenerateAudio}
                onDurationChange={(d) => updateConfig('in_duration', d)}
                onResolutionChange={handleResolutionChange}
                onAspectRatioChange={(r) => updateConfig('in_aspect_ratio', r)}
                onProviderGenerateAudioChange={(a) => updateConfig('in_provider_generate_audio', a)}
                allowBgm={allow_bgm}
                onAllowBgmChange={(b) => updateConfig('in_allow_bgm', b)}
                isRunning={showSpinner || generateLocked}
                onGenerate={handleGenerate}
                activeTab={effectiveBottomPanelTab}
                canGenerate={videoCanGenerate}
                cannotGenerateReason={generateBlockReason}
                modelUnavailableReason={videoModelUnavailableReason}
              />
            )
          }
          if (tab === 'variants') {
            return (
              <VideoVariantsTabContent
                buildPosterUrl={buildPosterUrl}
                history={historyEntries}
                pickedIndex={pickedIndex}
                focusRequestToken={variantsFocusRequestToken}
                onSelectVariant={handleHistorySelect}
                batchPending={batchPending}
                buildVideoUrl={buildVideoUrl}
                onDownloadVariant={(path, realIdx) => {
                  const { filename } = buildClipDownloadFilename({
                    canvasNodes: getNodes(),
                    canvasEdges: edges,
                    nodeId: id,
                    variantHistory: historyEntries,
                    pickedIndex: realIdx,
                    assetPath: path,
                    trigger: 'variants-panel',
                  })
                  downloadFile(path, filename, 'variants-panel', realIdx)
                }}
                defaultAspectRatio={cssAspectRatio}
                clampPortrait={false}
                nodeId={id}
                // RFC-1146 — favourite/dislike markers. `isActive` is left at
                // its default (true): UnifiedBottomPanel wraps the panel in
                // AnimatePresence and renders only the active tab, so this
                // component mounts exactly when the Variants view opens and
                // unmounts when it closes. Mount/unmount therefore already
                // gives the required "next time the variant box is opened"
                // semantics — fresh marker fetch, fresh open-time snapshot,
                // and the filter reset to its default.
                projectId={projectId ?? undefined}
              />
            )
          }
          return null
        }}
      </UnifiedBottomPanel>

      {/* Reference Selector modal */}
      {isRefSelectorOpen && !generationGrace.isLocallyGenerating && (
        <ReferenceSelector
          isOpen={isRefSelectorOpen}
          onClose={() => setIsRefSelectorOpen(false)}
          onSave={handleRefSave}
          excludeNodeId={id}
          canvasNodes={canvas?.getNodes() ?? getNodes()}
          currentReferences={currentRefs}
          maxReferences={referenceSelectorTotalLimit}
          maxReferencesByMediaType={referenceSelectorLimits}
          referenceCandidateFilter={isVideoGeneratorReferenceCandidate}
          // Refuse a pick that would exceed a per-media cap instead of silently
          // evicting the user's oldest reference. Enabled for the video picker
          // first: RFC-1202 declared Veo's cap of 3, which turned a path that
          // never evicted (no declared image limit) into one that drops a
          // reference on every 4th pick. Other nodes keep the FIFO swap until
          // this has been exercised here.
          refuseAtMediaLimit
          targetBackendType="video_generator"
          visibleTabs={referenceSelectorVisibleTabs}
          uploadMediaTypes={referenceSelectorUploadMediaTypes}
          onUploadFile={uploadFile ?? undefined}
        />
      )}

      {/* Video Preview Lightbox */}
      {isVideoModalOpen &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={videoUrl ?? undefined}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body,
        )}

      {upscaleVideoFlyout}
    </div>
  )
})

VideoGeneratorNode.displayName = 'VideoGeneratorNode'
import { useNodePresentationWidth, useNodePresentationPreview } from '../../NodePresentationContext'
